import React, { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { 
  Button, 
  Search, 
  Icon, 
  Dropdown, 
  ButtonIcon,
  Checkbox,
  TextArea
} from "pepsico-ds";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import forge from "node-forge";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoading } from "../../loader";
import "./sso_onboarding_details.css";

const MIN_CERT_VALIDITY_DAYS = 30;
const MIN_LOGO_WIDTH = 420;
const MIN_LOGO_HEIGHT = 120;
const ASSOCIATE_SSO_FAQ_URL = "https://pepsico.sharepoint.com/teams/SharedContent/SitePages/Associate-SSO-Integration-FAQ.aspx?ga=1";

const SSOOnboardingDetails = () => {
  const { t: baseT } = useTranslation('translation', { keyPrefix: 'sso_onboarding' });
  const t = useCallback((key, options) => {
    const translated = baseT(key, options);
    // If key is missing under keyPrefix, i18n returns "sso_onboarding.<key>".
    // Return the readable key text instead of exposing internal key paths in UI.
    if (typeof translated === 'string' && translated === `sso_onboarding.${key}`) {
      return key;
    }
    return translated;
  }, [baseT]);
  const navigate = useNavigate();
  const location = useLocation();
  const { REACT_APP_API_ENDPOINT, REACT_APP_CONST } = process.env;
  const { showLoader, hideLoader } = useLoading();
  const hasValidEntryState = Boolean(location.state?.businessInfo && location.state?.selectedType);

  // Get data from previous page
  const businessInfo = location.state?.businessInfo || {};
  const selectedType = location.state?.selectedType || 'saml';
  const normalizedEnvironment = (businessInfo.environment || '').trim().toLowerCase();
  const isNonProdEnvironment = normalizedEnvironment !== 'production' && normalizedEnvironment !== 'prod';
  const applicationUrlValidationOptions = isNonProdEnvironment
    ? { allowMissingProtocol: true }
    : {};
  const oidcRedirectUrlValidationOptions = selectedType === 'oidc' && isNonProdEnvironment
    ? { allowIpAddress: true, allowHttp: true, allowMissingProtocol: true, allowLocalhost: true }
    : {};

  React.useEffect(() => {
    if (!hasValidEntryState) {
      navigate('/sso-onboarding', { replace: true });
    }
  }, [hasValidEntryState, navigate]);

  // Form state - Only Application Details expanded initially
  const isBusinessInfoExpanded = true;
  const [isAppDetailsExpanded, setIsAppDetailsExpanded] = useState(true);
  const [isTechnicalConfigExpanded, setIsTechnicalConfigExpanded] = useState(false);
  const [isAdditionalDetailsExpanded, setIsAdditionalDetailsExpanded] = useState(false);
  const [isCertificationDetailsExpanded, setIsCertificationDetailsExpanded] = useState(false);
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);
  const [hasInteractedM2mEnvironment, setHasInteractedM2mEnvironment] = useState(false);
  const [hasInteractedElevatedPermissions, setHasInteractedElevatedPermissions] = useState(false);
  const [hasInteractedLogoChoice, setHasInteractedLogoChoice] = useState(false);
  const [hasInteractedAuthorizationRolesChoice, setHasInteractedAuthorizationRolesChoice] = useState(false);
  const [hasInteractedSigningCertChoice, setHasInteractedSigningCertChoice] = useState(false);
  const [hasInteractedOidcAdminChoice, setHasInteractedOidcAdminChoice] = useState(false);

  // Track which sections are completed
  const [completedSections, setCompletedSections] = useState({
    appDetails: false,
    accessControl: false,
    elevatedPermissions: false,
    appLogo: false,
    certificationDetails: false,
    technicalConfig: false,
    additionalDetails: false
  });

  // OIDC specific states
  const [loginUrls, setLoginUrls] = useState(['']);
  const [needLogoutUrl, setNeedLogoutUrl] = useState('no');
  const [logoutUrl, setLogoutUrl] = useState('');
  const [hasOwnLogo, setHasOwnLogo] = useState('yes'); // Default Yes per requirements
  const [isAdminApp, setIsAdminApp] = useState('');
  const [applicationLogo, setApplicationLogo] = useState(null);
  const fileInputRef = useRef(null);
  const autoRoleIdxValidatedRef = useRef('');

  // SAML specific states - Start collapsed
  const [isAccessControlExpanded, setIsAccessControlExpanded] = useState(false);
  const [isElevatedPermissionsExpanded, setIsElevatedPermissionsExpanded] = useState(false);
  const [isAppLogoExpanded, setIsAppLogoExpanded] = useState(false);
  const [needIdxAttribute, setNeedIdxAttribute] = useState('yes');
  const [hasExistingIdxAttribute, setHasExistingIdxAttribute] = useState('yes');
  const [idxAttributePrefix, setIdxAttributePrefix] = useState('pepapp');
  const [idxAttributeValue, setIdxAttributeValue] = useState('');
  const [idxAttributeStatus, setIdxAttributeStatus] = useState('');
  const [idxValidationStatus, setIdxValidationStatus] = useState(null); // null, 'validating', 'valid', 'invalid'
  const [needAuthorizationRoles, setNeedAuthorizationRoles] = useState('no');
  const samlFileInputRef = useRef(null);
  
  // Authorization Roles states
  const [authorizationRoles, setAuthorizationRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [showSamlPreview, setShowSamlPreview] = useState(false);
  const [hasExistingIdxForRole, setHasExistingIdxForRole] = useState('yes');
  const [roleIdxAttributeValue, setRoleIdxAttributeValue] = useState('');
  const [roleIdxValidationStatus, setRoleIdxValidationStatus] = useState(null);

  // New IDX Attribute Validated popup state
  const [showNewIdxValidatedPopup, setShowNewIdxValidatedPopup] = useState(false);
  const [newIdxValidatedType, setNewIdxValidatedType] = useState(null); // 'idx' or 'role'

  // Success Modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [copyButtonText, setCopyButtonText] = useState('Copy');

  // Application Name validation state
  const [appNameValidationStatus, setAppNameValidationStatus] = useState(null); // null, 'validating', 'valid', 'invalid'

  // M2M specific states
  const [m2mEnvironment, setM2mEnvironment] = useState('public');
  const [customScopes, setCustomScopes] = useState([]);
  const [newScopeName, setNewScopeName] = useState('');

  // Signing Certificate states (SAML, only for Confidential/Restricted)
  const [hasSigningCert, setHasSigningCert] = useState('yes');
  const [signingCertEntryMode, setSigningCertEntryMode] = useState('file');
  const [signingCertFile, setSigningCertFile] = useState(null);
  const [signingCertText, setSigningCertText] = useState('');
  const [signingCertTextValidated, setSigningCertTextValidated] = useState(false);
  const [signingCertDetails, setSigningCertDetails] = useState(null);
  const [showSigningCertDetailsPopup, setShowSigningCertDetailsPopup] = useState(false);
  const [signingCertDaysRemaining, setSigningCertDaysRemaining] = useState(null);
  const [businessJustification, setBusinessJustification] = useState('');
  const signingCertInputRef = useRef(null);

  // Logout URL as array (Max 3, per requirements)
  const [logoutUrls, setLogoutUrls] = useState(['']);

  // Tooltip state - tracks which field is focused
  const [focusedField, setFocusedField] = useState(null);

  // Validation errors for non-RHF fields
  const [fieldErrors, setFieldErrors] = useState({
    loginUrls: {},
    logoutUrls: {},
    logoutUrl: '',
    idxAttributeValue: '',
    roleIdxAttributeValue: '',
    newRoleName: '',
    newScopeName: '',
    applicationName: '',
    businessJustification: '',
    signingCert: '',
  });

  // Validation patterns and helpers
  const validators = {
    // HTTPS-only URL pattern
    urlPattern: /^https:\/\/[\w.-]+(?::\d+)?(?:\/[^\s]*)?$/i,
    // HTTP/HTTPS URL pattern
    urlPatternHttpOrHttps: /^https?:\/\/[\w.-]+(?::\d+)?(?:\/[^\s]*)?$/i,
    // URI/Entity ID - no whitespace allowed (per spec)
    uriPattern: /^\S+$/,
    // Email
    emailPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    // Application Name - no leading/trailing space, only _ or - as special chars
    // Pattern: starts/ends non-space, allows letters/digits/space/_/-
    appNamePattern: /^[a-zA-Z0-9_-](?:[a-zA-Z0-9 _-]*[a-zA-Z0-9_-])?$/,
    // IDX/Role attribute - alphanumeric, 2-8 characters
    idxAttributePattern: /^[a-zA-Z0-9]{2,8}$/,
    // Role name - alphanumeric, 4-64 chars, allows _ - ( )
    rolePattern: /^[a-zA-Z0-9_()-]{4,64}$/,
    // Scope name - no quotes, spaces, forward/backward slashes
    scopePattern: /^[^"'\s/\\]+$/,
    // Signing Certificate (PEM format)
    certPattern: /^-----BEGIN CERTIFICATE-----[\s\S]+-----END CERTIFICATE-----\s*$/,
  };

  const normalizeUrlWithDefaultProtocol = (value, opts = {}) => {
    const { allowMissingProtocol = false } = opts;
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) return '';

    if (!allowMissingProtocol) {
      return trimmedValue;
    }

    // For non-prod URL fields, treat protocol-less input as HTTPS.
    const hasProtocol = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(trimmedValue);
    return hasProtocol ? trimmedValue : `https://${trimmedValue}`;
  };

  // URL validation helper - blocks okta.com, http:, whitespace, localhost
  const validateUrl = (value, label = 'URL', opts = {}) => {
    const { allowOkta = false, allowIpAddress = false, allowHttp = false, allowLocalhost = false } = opts;
    if (!value || !value.trim()) return `${label} is required`;
    const trimmed = value.trim();
    const normalizedForValidation = normalizeUrlWithDefaultProtocol(trimmed, opts);
    if (/\s/.test(value)) return `${label} cannot contain whitespace`;
    if (!allowHttp && /^http:\/\//i.test(normalizedForValidation)) return `${label} cannot use http:// (use https://)`;
    if (!allowLocalhost && /localhost/i.test(normalizedForValidation)) return `${label} cannot contain localhost`;
    if (!allowOkta && /okta\.com/i.test(normalizedForValidation)) return `${label} cannot contain okta.com`;
    const urlPatternToUse = allowHttp ? validators.urlPatternHttpOrHttps : validators.urlPattern;
    if (!urlPatternToUse.test(normalizedForValidation)) return `Please enter ${label}`;
    try {
      const parsed = new URL(normalizedForValidation);
      const hostname = parsed.hostname?.toLowerCase() || '';

      if (allowLocalhost && hostname === 'localhost') {
        return '';
      }

      const isIpv4Hostname = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
      const hasValidIpv4Range = isIpv4Hostname && hostname.split('.').every((segment) => {
        const valueAsNumber = Number(segment);
        return segment !== '' && valueAsNumber >= 0 && valueAsNumber <= 255;
      });

      if (hasValidIpv4Range) {
        if (allowIpAddress) {
          return '';
        }
        return `${label} must include a valid domain suffix (e.g. .com, .in, .org)`;
      }

      const hostParts = hostname.split('.').filter(Boolean);
      const hasValidDomainSuffix = hostParts.length > 1 && hostParts[hostParts.length - 1].length >= 2;
      if (!hasValidDomainSuffix) {
        return `${label} must include a valid domain suffix (e.g. .com, .in, .org)`;
      }
    } catch {
      return `Please enter a valid ${label} (e.g. https://example.com)`;
    }
    return '';
  };

  const areUrlsEquivalent = (firstUrl, secondUrl) => {
    if (!firstUrl || !secondUrl) return false;

    const normalizeUrlForComparison = (urlValue) => {
      const trimmed = normalizeUrlWithDefaultProtocol(urlValue, { allowMissingProtocol: true });
      try {
        const parsed = new URL(trimmed);
        const protocol = parsed.protocol.toLowerCase();
        const hostname = parsed.hostname.toLowerCase();
        const port = parsed.port ? `:${parsed.port}` : '';
        const pathname = (parsed.pathname || '/').replace(/\/+$/, '') || '/';
        const search = parsed.search || '';
        return `${protocol}//${hostname}${port}${pathname}${search}`;
      } catch {
        return trimmed.replace(/\/+$/, '').toLowerCase();
      }
    };

    return normalizeUrlForComparison(firstUrl) === normalizeUrlForComparison(secondUrl);
  };

  const getDuplicateUrlErrors = (urls, label, opts = {}) => {
    const duplicateErrors = {};
    const seenUrls = [];

    urls.forEach((urlValue, index) => {
      const trimmedValue = (urlValue || '').trim();
      if (!trimmedValue) return;

      // Only apply duplicate checks for URLs that already pass individual URL validation.
      if (validateUrl(trimmedValue, label, opts)) return;

      const existingMatch = seenUrls.find((seenUrl) => areUrlsEquivalent(seenUrl.value, trimmedValue));
      if (existingMatch) {
        const duplicateMessage = t(`Duplicate ${label} is not allowed`);
        duplicateErrors[index] = duplicateMessage;
      } else {
        seenUrls.push({ index, value: trimmedValue });
      }
    });

    return duplicateErrors;
  };

  const getUrlFieldErrorsByIndex = (urls, label, opts = {}) => {
    const nextErrors = {};

    urls.forEach((urlValue, index) => {
      const trimmedValue = (urlValue || '').trim();
      if (!trimmedValue) return;

      const urlError = validateUrl(trimmedValue, label, opts);
      if (urlError) {
        nextErrors[index] = urlError;
      }
    });

    const duplicateErrors = getDuplicateUrlErrors(urls, label, opts);
    Object.keys(duplicateErrors).forEach((key) => {
      nextErrors[key] = duplicateErrors[key];
    });

    return nextErrors;
  };

  // Data Classification helpers
  const dataClassification = (businessInfo.dataClassification || '').toUpperCase();
  const isPepsicoInternal = dataClassification.includes('INTERNAL');
  const isRestrictedOrConfidential = dataClassification.includes('RESTRICTED') || dataClassification.includes('CONFIDENTIAL');

  // Tooltip messages for each field
  const tooltipMessages = {
    applicationName: t('tooltip_applicationName'),
    applicationUrl: t('tooltip_applicationUrl'),
    assertionConsumerServiceUrl: t('tooltip_acsUrl'),
    audienceUri: t('tooltip_audienceUri'),
    nameIdFormat: t('tooltip_nameIdFormat'),
    applicationType: t('tooltip_applicationType'),
    loginUrl: t('tooltip_loginUrl'),
    idxAttributeHelp: t('Please provide your IDX Attribute.'),
    derivedRoleIdxHelp: t('Derived IDX Attribute'),
    validateButton: t('Validates if the IDX attribute already exists or not.'),
    derivedRoleIdxValidateButton: t('This attribute is derived from the attribute name provided above, it is used to enable role based access to your application.'),
  };

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { control, handleSubmit, formState: { errors, isValid }, getValues, watch, clearErrors, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      applicationName: "",
      audienceUri: "",
      applicationUrl: "",
      nameIdFormat: [],
      assertionConsumerServiceUrl: "",
      businessJustification: "",
      // OIDC specific fields
      applicationType: [],
      applicationAccessControl: [],
      applicationServiceType: [],
    },
  });

  const watchedApplicationName = watch("applicationName");
  const watchedApplicationUrl = watch("applicationUrl");
  const watchedAssertionConsumerServiceUrl = watch("assertionConsumerServiceUrl");
  const watchedAudienceUri = watch("audienceUri");
  const watchedNameIdFormat = watch("nameIdFormat");
  const watchedApplicationType = watch("applicationType");
  const watchedApplicationAccessControl = watch("applicationAccessControl");
  const isApplicationNameValidateDisabled =
    appNameValidationStatus === 'validating' ||
    appNameValidationStatus === 'valid' ||
    !watchedApplicationName?.trim() ||
    !!errors.applicationName;
  const shouldSyncRoleIdxFromValidatedIdx =
    needIdxAttribute === 'yes' &&
    needAuthorizationRoles === 'yes' &&
    hasExistingIdxForRole === 'no' &&
    !!idxAttributeValue?.trim();
  const shouldLockRoleIdxToDerivedNo =
    hasExistingIdxAttribute === 'no' && idxValidationStatus === 'valid';
  const shouldDisableRoleIdxExistingYesOption =
    shouldLockRoleIdxToDerivedNo;
  const existingIdxAttributePattern = /^pepapp[a-zA-Z0-9]{2,13}status$/;
  const existingRoleIdxAttributePattern = /^pepapp[a-zA-Z0-9]{2,13}roles$/;

  const handleIdxAttributeValueChange = (value) => {
    const trimmed = value.trim();
    setIdxAttributeValue(value);
    if (idxValidationStatus === 'valid') setIdxValidationStatus(null);

    if (!trimmed) {
      setFieldErrors((p) => ({ ...p, idxAttributeValue: '' }));
      return;
    }

    if (hasExistingIdxAttribute === 'yes') {
      if (!existingIdxAttributePattern.test(trimmed)) {
        setFieldErrors((p) => ({ ...p, idxAttributeValue: t('Existing IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + status') }));
      } else {
        setFieldErrors((p) => ({ ...p, idxAttributeValue: '' }));
      }
      return;
    }

    if (!validators.idxAttributePattern.test(trimmed)) {
      setFieldErrors((p) => ({ ...p, idxAttributeValue: t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters') }));
    } else {
      setFieldErrors((p) => ({ ...p, idxAttributeValue: '' }));
    }
  };

  const getSharedIdxCoreValue = () => {
    const trimmed = (idxAttributeValue || '').trim();
    if (!trimmed) return '';

    const statusMatch = trimmed.match(/^pepapp([a-zA-Z0-9]{2,13})status$/);
    if (statusMatch) return statusMatch[1];

    const roleMatch = trimmed.match(/^pepapp([a-zA-Z0-9]{2,13})roles$/);
    if (roleMatch) return roleMatch[1];

    // Normalize in-progress/manual values so we don't duplicate prefix/suffix
    // when deriving the role IDX preview/payload.
    let normalized = trimmed;
    const prefix = (idxAttributePrefix || '').toLowerCase();
    if (prefix && normalized.toLowerCase().startsWith(prefix)) {
      normalized = normalized.slice(idxAttributePrefix.length);
    }
    if (normalized.toLowerCase().endsWith('status')) {
      normalized = normalized.slice(0, -6);
    } else if (normalized.toLowerCase().endsWith('roles')) {
      normalized = normalized.slice(0, -5);
    }
    if (normalized) return normalized;

    if (validators.idxAttributePattern.test(trimmed)) return trimmed;
    return trimmed;
  };

  const getDerivedRoleIdxInputValue = () => {
    if (hasExistingIdxForRole === 'yes') {
      return roleIdxAttributeValue || '';
    }
    const core = getSharedIdxCoreValue();
    if (!core) return '';
    return core;
  };

  const handleRoleIdxAttributeValueChange = (value) => {
    const trimmed = value.trim();
    setRoleIdxAttributeValue(value);
    if (roleIdxValidationStatus === 'valid') {
      setRoleIdxValidationStatus(null);
    }

    if (!trimmed) {
      setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: '' }));
      return;
    }

    if (hasExistingIdxForRole === 'yes') {
      if (!existingRoleIdxAttributePattern.test(trimmed)) {
        setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: t('Existing Role IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + roles') }));
      } else {
        setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: '' }));
      }
      return;
    }

    if (!validators.idxAttributePattern.test(trimmed)) {
      setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters') }));
    } else {
      setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: '' }));
    }
  };

  const getDerivedRoleIdxPayloadValue = () => {
    const derived = getDerivedRoleIdxInputValue().trim();
    if (!derived) return null;
    return hasExistingIdxForRole === 'yes' ? derived : formatRoleIdxWithPrefixAndSuffix(derived);
  };

  const formatRoleIdxWithPrefixAndSuffix = (value) => {
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) return '';

    const prefix = idxAttributePrefix || '';
    const lowerPrefix = prefix.toLowerCase();
    let normalized = trimmedValue;

    if (lowerPrefix && normalized.toLowerCase().startsWith(lowerPrefix)) {
      normalized = normalized.slice(prefix.length);
    }

    if (normalized.toLowerCase().endsWith('roles')) {
      normalized = normalized.slice(0, -5);
    }

    return `${prefix}${normalized}roles`;
  };

  const getIdxAttributePayloadValue = () => {
    const trimmed = (idxAttributeValue || '').trim();
    if (!trimmed) return null;
    return hasExistingIdxAttribute === 'yes'
      ? trimmed
      : `${idxAttributePrefix}${trimmed}status`;
  };

  // Manual check function for Application Details completion
  const checkAppDetailsCompletion = () => {
    const values = getValues();
    const trimmedApplicationName = values.applicationName?.trim() || '';
    const trimmedApplicationUrl = values.applicationUrl?.trim() || '';
    const trimmedAssertionConsumerServiceUrl = values.assertionConsumerServiceUrl?.trim() || '';
    const trimmedAudienceUri = values.audienceUri?.trim() || '';
    const hasValidApplicationUrl = !!trimmedApplicationUrl && !validateUrl(trimmedApplicationUrl, 'Application Login URL', applicationUrlValidationOptions);
    const hasValidAssertionConsumerServiceUrl = !!trimmedAssertionConsumerServiceUrl && !validateUrl(trimmedAssertionConsumerServiceUrl, 'ACS URL');
    const hasDistinctAppAndAcsUrls = !areUrlsEquivalent(trimmedApplicationUrl, trimmedAssertionConsumerServiceUrl);
    const hasValidAudienceUri = !!trimmedAudienceUri && !/\s/.test(trimmedAudienceUri);
    
    if (selectedType === 'saml') {
      // SAML required fields - check if they have values
      const isComplete = 
        !!trimmedApplicationName &&
        appNameValidationStatus === 'valid' &&
        hasValidApplicationUrl &&
        hasValidAssertionConsumerServiceUrl &&
        hasDistinctAppAndAcsUrls &&
        hasValidAudienceUri &&
        Array.isArray(values.nameIdFormat) && values.nameIdFormat.length > 0;
      
      if (isComplete && !completedSections.appDetails) {
        setCompletedSections(prev => ({ ...prev, appDetails: true }));
        // Auto-expand next section and keep current section open
        setIsAccessControlExpanded(true);
      }
    } else if (selectedType === 'oidc') {
      // OIDC required fields - check if they have values
      const isComplete = 
        !!trimmedApplicationName &&
        appNameValidationStatus === 'valid' &&
        Array.isArray(values.applicationType) && values.applicationType.length > 0 &&
        hasValidApplicationUrl;
      
      if (isComplete && !completedSections.appDetails) {
        setCompletedSections(prev => ({ ...prev, appDetails: true }));
        // Auto-expand next section and keep current section open
        setIsTechnicalConfigExpanded(true);
      }
    } else if (selectedType === 'oidc-m2m') {
      const isComplete =
        !!trimmedApplicationName &&
        appNameValidationStatus === 'valid' &&
        ((m2mEnvironment === 'private' && customScopes.length > 0) || (m2mEnvironment === 'public' && hasInteractedM2mEnvironment));

      if (isComplete && !completedSections.appDetails) {
        setCompletedSections(prev => ({ ...prev, appDetails: true }));
      }
    }
  };

  // Check if Application Details section is complete and auto-expand next accordion
  React.useEffect(() => {
    checkAppDetailsCompletion();
  }, [
    selectedType,
    completedSections.appDetails,
    watchedApplicationName,
    watchedApplicationUrl,
    watchedAssertionConsumerServiceUrl,
    watchedAudienceUri,
    watchedNameIdFormat,
    watchedApplicationType,
    appNameValidationStatus,
    m2mEnvironment,
    customScopes.length,
    hasInteractedM2mEnvironment,
  ]);

  // Check if Access Control section is complete and auto-expand next accordion
  React.useEffect(() => {
    const checkAccessControl = () => {
      const selectedAccessControl = getValues('applicationAccessControl');
      const hasAccessControlSelection = Array.isArray(selectedAccessControl) && selectedAccessControl.length > 0;
      const hasValidRoleIdxAttribute =
        !!roleIdxAttributeValue?.trim() &&
        validators.idxAttributePattern.test(roleIdxAttributeValue.trim()) &&
        roleIdxValidationStatus === 'valid';
      const rolesSectionComplete =
        needIdxAttribute === 'no' ||
        (needAuthorizationRoles === 'no' && hasInteractedAuthorizationRolesChoice) ||
        (needAuthorizationRoles === 'yes' &&
          hasInteractedAuthorizationRolesChoice &&
          hasValidRoleIdxAttribute &&
          authorizationRoles.length > 0);

      if (selectedType === 'saml') {
        // For SAML: Check if IDX attribute fields are filled with minimum length
        const baseAccessControlComplete = 
          (needIdxAttribute === 'no' && hasAccessControlSelection) || 
          (needIdxAttribute === 'yes' && hasExistingIdxAttribute === 'yes' && idxAttributeValue?.trim()?.length >= 3 && idxValidationStatus === 'valid') ||
          (needIdxAttribute === 'yes' && hasExistingIdxAttribute === 'no' && idxAttributeValue?.trim()?.length >= 3);
        const isAccessControlComplete = baseAccessControlComplete && rolesSectionComplete;
        
        if (isAccessControlComplete && (completedSections.appDetails || isAccessControlExpanded) && !completedSections.accessControl) {
          setCompletedSections(prev => ({ ...prev, accessControl: true }));
          // Auto-expand next section and keep current section open
          setIsElevatedPermissionsExpanded(true);
        }
      } else if (selectedType === 'oidc') {
        // For OIDC: Check if IDX attribute fields are filled with minimum length
        const baseAccessControlComplete = 
          (needIdxAttribute === 'no' && hasAccessControlSelection) || 
          (needIdxAttribute === 'yes' && hasExistingIdxAttribute === 'yes' && idxAttributeValue?.trim()?.length >= 3 && idxValidationStatus === 'valid') ||
          (needIdxAttribute === 'yes' && hasExistingIdxAttribute === 'no' && idxAttributeValue?.trim()?.length >= 3);
        const isAccessControlComplete = baseAccessControlComplete && rolesSectionComplete;
        
        if (isAccessControlComplete && (completedSections.appDetails || isAccessControlExpanded) && !completedSections.accessControl) {
          setCompletedSections(prev => ({ ...prev, accessControl: true }));
          // Auto-expand next section and keep current section open
          setIsAdditionalDetailsExpanded(true);
        }
      }
    };
    
    const timeoutId = setTimeout(checkAccessControl, 500);
    return () => clearTimeout(timeoutId);
  }, [needIdxAttribute, hasExistingIdxAttribute, idxAttributeValue, idxValidationStatus, needAuthorizationRoles, hasInteractedAuthorizationRolesChoice, hasExistingIdxForRole, authorizationRoles, roleIdxAttributeValue, roleIdxValidationStatus, watchedApplicationAccessControl, selectedType, completedSections, isAccessControlExpanded]);

  // Check if OIDC Technical Config section is complete and auto-expand Access Control
  React.useEffect(() => {
    const checkTechnicalConfig = () => {
      if (selectedType === 'oidc' && completedSections.appDetails && !completedSections.technicalConfig) {
        const validLoginUrls = loginUrls.filter(url => url && url.trim());
        const validLogoutUrls = logoutUrls.filter(url => url && url.trim());
        const hasValidLoginUrls = validLoginUrls.length > 0 && validLoginUrls.every(url => !validateUrl(url, 'Login URL', oidcRedirectUrlValidationOptions));
        const hasValidLogoutUrls = validLogoutUrls.length > 0 && validLogoutUrls.every(url => !validateUrl(url, 'Logout URL', oidcRedirectUrlValidationOptions));
        const hasUniqueLoginUrls = Object.keys(getDuplicateUrlErrors(loginUrls, 'Login URL', oidcRedirectUrlValidationOptions)).length === 0;
        const hasUniqueLogoutUrls = Object.keys(getDuplicateUrlErrors(logoutUrls, 'Logout URL', oidcRedirectUrlValidationOptions)).length === 0;
        
        if (hasValidLoginUrls && hasValidLogoutUrls && hasUniqueLoginUrls && hasUniqueLogoutUrls) {
          setCompletedSections(prev => ({ ...prev, technicalConfig: true }));
          // Auto-expand next section and keep current section open
          setIsAccessControlExpanded(true);
        }
      }
    };
    
    const timeoutId = setTimeout(checkTechnicalConfig, 500);
    return () => clearTimeout(timeoutId);
  }, [loginUrls, logoutUrls, selectedType, completedSections]);

  // Auto-expand App Logo after Elevated Permissions is interacted with (SAML)
  React.useEffect(() => {
    const checkElevatedPermissions = () => {
      if (selectedType === 'saml' && completedSections.accessControl && !completedSections.elevatedPermissions) {
        if (hasInteractedElevatedPermissions) {
          setCompletedSections(prev => ({ ...prev, elevatedPermissions: true }));
          setIsAppLogoExpanded(true);
        }
      }
    };
    
    const timeoutId = setTimeout(checkElevatedPermissions, 300);
    return () => clearTimeout(timeoutId);
  }, [isAdminApp, hasInteractedElevatedPermissions, selectedType, completedSections]);

  // Auto-expand Certification Details after App Logo is handled (SAML)
  React.useEffect(() => {
    const checkAppLogo = () => {
      if (selectedType === 'saml' && completedSections.elevatedPermissions && !completedSections.appLogo) {
        const logoComplete =
          (hasOwnLogo === 'no' && hasInteractedLogoChoice) ||
          (hasOwnLogo === 'yes' && applicationLogo);
        if (logoComplete) {
          setCompletedSections(prev => ({ ...prev, appLogo: true }));
          if (isRestrictedOrConfidential) {
            setIsCertificationDetailsExpanded(true);
          }
        }
      }
    };
    
    const timeoutId = setTimeout(checkAppLogo, 300);
    return () => clearTimeout(timeoutId);
  }, [hasOwnLogo, applicationLogo, hasInteractedLogoChoice, selectedType, completedSections, isRestrictedOrConfidential]);

  // Auto-complete Additional Details for OIDC
  React.useEffect(() => {
    const checkAdditionalDetails = () => {
      if (selectedType === 'oidc' && completedSections.accessControl && !completedSections.additionalDetails) {
        const logoComplete =
          (hasOwnLogo === 'no' && hasInteractedLogoChoice) ||
          (hasOwnLogo === 'yes' && applicationLogo);
        
        if (logoComplete && hasInteractedOidcAdminChoice) {
          setCompletedSections(prev => ({ ...prev, additionalDetails: true }));
        }
      }
    };
    
    const timeoutId = setTimeout(checkAdditionalDetails, 300);
    return () => clearTimeout(timeoutId);
  }, [hasOwnLogo, applicationLogo, hasInteractedLogoChoice, hasInteractedOidcAdminChoice, selectedType, completedSections]);

  React.useEffect(() => {
    const checkCertificationDetails = () => {
      if (selectedType === 'saml' && isRestrictedOrConfidential && completedSections.appLogo && !completedSections.certificationDetails) {
        const hasSigningCertInput = !!signingCertFile || !!signingCertText.trim();
        const certificationComplete =
          (hasSigningCert === 'yes' && hasSigningCertInput && signingCertDaysRemaining !== null && signingCertDaysRemaining >= MIN_CERT_VALIDITY_DAYS) ||
          (hasSigningCert === 'no' && hasInteractedSigningCertChoice && businessJustification.trim().length >= 10);

        if (certificationComplete) {
          setCompletedSections(prev => ({ ...prev, certificationDetails: true }));
        }
      }
    };

    const timeoutId = setTimeout(checkCertificationDetails, 300);
    return () => clearTimeout(timeoutId);
  }, [hasSigningCert, hasInteractedSigningCertChoice, signingCertFile, signingCertText, signingCertDaysRemaining, businessJustification, selectedType, isRestrictedOrConfidential, completedSections]);

  React.useEffect(() => {
    // New IDX validation means role IDX must be the derived "No" path.
    if (shouldLockRoleIdxToDerivedNo && hasExistingIdxForRole !== 'no') {
      setHasExistingIdxForRole('no');
      setRoleIdxAttributeValue('');
    }

    if (shouldSyncRoleIdxFromValidatedIdx) {
      const syncedValue = getDerivedRoleIdxInputValue();
      if (roleIdxAttributeValue !== syncedValue) {
        autoRoleIdxValidatedRef.current = '';
        setRoleIdxAttributeValue(syncedValue);
        setRoleIdxValidationStatus(
          hasExistingIdxAttribute === 'no' && idxValidationStatus === 'valid' ? 'valid' : null
        );
        setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: '' }));
        return;
      }
      // Keep derived role value synced, but do not auto-validate.
      // Role validation must run only on explicit Validate button action.
      autoRoleIdxValidatedRef.current = syncedValue || '';
      return;
    }

    autoRoleIdxValidatedRef.current = '';

    if (
      needAuthorizationRoles === 'yes' &&
      hasExistingIdxForRole === 'no' &&
      roleIdxAttributeValue === getDerivedRoleIdxInputValue() &&
      idxValidationStatus !== 'valid' &&
      roleIdxValidationStatus === 'valid'
    ) {
      setRoleIdxValidationStatus(null);
    }
  }, [
    shouldLockRoleIdxToDerivedNo,
    shouldSyncRoleIdxFromValidatedIdx,
    idxAttributeValue,
    idxValidationStatus,
    needAuthorizationRoles,
    hasExistingIdxForRole,
    roleIdxAttributeValue,
    roleIdxValidationStatus,
  ]);

  // Reusable validation rules for RHF Controllers
  const validationRules = {
    applicationName: {
      required: t('Application Name is required'),
      minLength: { value: 3, message: t('Application Name must be at least 3 characters') },
      maxLength: { value: 30, message: t('Application Name must not exceed 30 characters') },
      validate: (val) => {
        if (!val) return true;
        if (val !== val.trim()) return t('Application Name cannot start or end with whitespace');
        if (/[^a-zA-Z0-9]+(?:qa|prod|dev|ite|non[\s_-]*prod)\b/i.test(val)) {
          return t("Application Name cannot contain a special character followed by qa, non prod, prod, dev, or ite.");
        }
        if (!validators.appNamePattern.test(val)) return t('Application Name can only contain alphanumeric, spaces, hyphens (-) and underscores (_)');
        return true;
      },
    },
    applicationUrl: {
      required: t('Application URL is required'),
      validate: (val) => {
        const err = validateUrl(val, 'Application URL', applicationUrlValidationOptions);
        if (err) return err;

        const acsUrl = getValues('assertionConsumerServiceUrl');
        if (acsUrl?.trim() && !validateUrl(acsUrl, 'ACS URL') && areUrlsEquivalent(val, acsUrl)) {
          return t('The Application URL is what is used by your users to access the application, the ACS URL is a location which is used by Okta only (they should not be the same)');
        }

        return err ? err : true;
      },
    },
    assertionConsumerServiceUrl: {
      required: t('ACS URL is required'),
      validate: (val) => {
        const err = validateUrl(val, 'ACS URL');
        if (err) return err;

        const applicationUrl = getValues('applicationUrl');
        if (applicationUrl?.trim() && !validateUrl(applicationUrl, 'Application Login URL', applicationUrlValidationOptions) && areUrlsEquivalent(val, applicationUrl)) {
          return t('The Application URL is what is used by your users to access the application, the ACS URL is a location which is used by Okta only (they should not be the same)');
        }

        return err ? err : true;
      },
    },
    audienceUri: {
      required: t('Audience URI is required'),
      maxLength: { value: 255, message: t('Audience URI must not exceed 255 characters') },
      validate: (val) => {
        if (!val) return true;
        if (/\s/.test(val)) return t('Audience URI cannot contain whitespace');
        return true;
      },
    },
    nameIdFormat: {
      required: t('Name ID Format is required'),
      validate: (val) => (Array.isArray(val) ? val.length > 0 : !!val) || t('Name ID Format is required'),
    },
    applicationType: {
      required: t('Application Type is required'),
      validate: (val) => (Array.isArray(val) ? val.length > 0 : !!val) || t('Application Type is required'),
    },
    applicationServiceType: {
      required: t('Application/Service Type is required'),
      validate: (val) => (Array.isArray(val) ? val.length > 0 : !!val) || t('Application/Service Type is required'),
    },
    businessJustification: {
      required: t('Business Justification is required'),
      minLength: { value: 10, message: t('Business Justification must be at least 10 characters') },
      maxLength: { value: 500, message: t('Business Justification must not exceed 500 characters') },
    },
  };

  // Name ID Format options (SAML) - per requirements
  const nameIdFormatOptions = [
    { id: "Unspecified", displayText: "Unspecified" },
    { id: "EmailAddress", displayText: "EmailAddress" },
    { id: "Persistent", displayText: "Persistent" },
    // { id: "Transient", displayText: "Transient" },
  ];

  // Application Type options (OIDC) - per requirements
  const applicationTypeOptions = [
    { id: "SINGLE_PAGE_APPLICATION", displayText: "Single Page Application" },
    { id: "WEB_APPLICATION", displayText: "Web Application" },
    { id: "NATIVE_APPLICATION", displayText: "Native Application" },
  ];

  // Application Access Control options (OIDC/SAML) - per requirements
  const applicationAccessControlOptions = [
    { id: "PEPSICO_INTERNAL", displayText: "Internal" },
    { id: "PEPSICO_EMPLOYEES", displayText: "Employee" },
    { id: "PEPSICO_CONTRACTORS", displayText: "Contractors" },
    { id: "PEPSICO_THIRDPARTY", displayText: "Vendors (Third Party)" },
  ];

  // Handle login URL changes - blocks okta.com, http:, whitespace, localhost
  const handleLoginUrlChange = (index, value) => {
    const newUrls = [...loginUrls];
    newUrls[index] = value;
    setLoginUrls(newUrls);
    const nextLoginErrors = getUrlFieldErrorsByIndex(newUrls, 'Login URL', oidcRedirectUrlValidationOptions);
    setFieldErrors((prev) => ({
      ...prev,
      loginUrls: nextLoginErrors,
    }));
  };

  const addLoginUrl = () => {
    if (loginUrls.length < 3) {
      setLoginUrls([...loginUrls, '']);
    }
  };

  const removeLoginUrl = (index) => {
    if (loginUrls.length > 1) {
      const newUrls = loginUrls.filter((_, i) => i !== index);
      setLoginUrls(newUrls);
      const nextLoginErrors = getUrlFieldErrorsByIndex(newUrls, 'Login URL', oidcRedirectUrlValidationOptions);
      setFieldErrors((prev) => {
        return { ...prev, loginUrls: nextLoginErrors };
      });
    }
  };

  // Handle file upload
  const validateLogoDimensions = (file) => new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      const img = new Image();

      img.onload = () => {
        resolve(img.width >= MIN_LOGO_WIDTH && img.height >= MIN_LOGO_HEIGHT);
      };

      img.onerror = () => {
        resolve(false);
      };

      img.src = String(loadEvent.target?.result || '');
    };

    reader.onerror = () => {
      resolve(false);
    };

    reader.readAsDataURL(file);
  });

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif'];
      const maxSize = 1024 * 1024; // 1MB
      
      if (!validTypes.includes(file.type)) {
        toast.error(t('Invalid file type. Please upload PNG, JPG, SVG, or GIF.'));
        return;
      }
      
      if (file.size > maxSize) {
        toast.error(t('File size must be less than 1MB.'));
        return;
      }

      const hasMinimumDimensions = await validateLogoDimensions(file);
      if (!hasMinimumDimensions) {
        toast.error(t('Logo must be at least 420px by 120px to prevent upscaling.'));
        return;
      }
      
      setApplicationLogo(file);
    }
  };

  const handleLogoDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload({ target: { files: [file] } });
    }
  };

  const removeLogoFile = () => {
    setApplicationLogo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (samlFileInputRef.current) {
      samlFileInputRef.current.value = '';
    }
  };

  // Handle Signing Certificate upload (SAML)
  const getCertificateDaysRemaining = (pemContent) => {
    try {
      const certificate = forge.pki.certificateFromPem(pemContent);
      const notAfter = certificate?.validity?.notAfter;
      if (!(notAfter instanceof Date) || Number.isNaN(notAfter.getTime())) {
        return null;
      }
      const msPerDay = 1000 * 60 * 60 * 24;
      return Math.ceil((notAfter.getTime() - Date.now()) / msPerDay);
    } catch (error) {
      return null;
    }
  };

  const formatCertificateName = (attributes = []) => {
    if (!Array.isArray(attributes)) return '-';
    const parts = attributes
      .map((attr) => {
        const key = attr?.shortName || attr?.name || '';
        const value = attr?.value || '';
        if (!key || !value) return null;
        return `${key}=${value}`;
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
  };

  const getDecodedCertificateDetails = (pemContent, daysRemaining) => {
    try {
      const certificate = forge.pki.certificateFromPem(pemContent);
      return {
        subject: formatCertificateName(certificate?.subject?.attributes),
        issuer: formatCertificateName(certificate?.issuer?.attributes),
        serialNumber: certificate?.serialNumber || '-',
        validFrom: certificate?.validity?.notBefore ? new Date(certificate.validity.notBefore).toLocaleString() : '-',
        validTo: certificate?.validity?.notAfter ? new Date(certificate.validity.notAfter).toLocaleString() : '-',
        daysRemaining,
      };
    } catch (error) {
      return null;
    }
  };

  const processSigningCertificateContent = (pemContent, { source = 'file', file = null, showToast = true } = {}) => {
    if (source === 'file' && !validators.certPattern.test(pemContent)) {
      setSigningCertFile(null);
      setSigningCertDaysRemaining(null);
      setSigningCertDetails(null);
      setShowSigningCertDetailsPopup(false);
      setSigningCertTextValidated(false);
      const errorMsg = t('Invalid certificate format. Must be a valid PEM-format certificate.');
      setFieldErrors((p) => ({ ...p, signingCert: errorMsg }));
      if (showToast) toast.error(errorMsg);
      return false;
    }

    const remainingDays = getCertificateDaysRemaining(pemContent);
    if (remainingDays === null) {
      setSigningCertFile(null);
      setSigningCertDaysRemaining(null);
      setSigningCertDetails(null);
      setShowSigningCertDetailsPopup(false);
      setSigningCertTextValidated(false);
      const errorMsg = t('Unable to read certificate validity period. Upload a valid certificate.');
      setFieldErrors((p) => ({ ...p, signingCert: errorMsg }));
      if (showToast) toast.error(errorMsg);
      return false;
    }

    if (remainingDays < MIN_CERT_VALIDITY_DAYS) {
      const errorMsg = t('Signing Certificate validity must be at least 30 days from today.');
      setSigningCertFile(null);
      setSigningCertDaysRemaining(remainingDays);
      setSigningCertDetails(null);
      setShowSigningCertDetailsPopup(false);
      setSigningCertTextValidated(false);
      setFieldErrors((p) => ({ ...p, signingCert: errorMsg }));
      if (showToast) toast.error(errorMsg);
      return false;
    }

    setFieldErrors((p) => ({ ...p, signingCert: '' }));
    setSigningCertDaysRemaining(remainingDays);
    setSigningCertDetails(getDecodedCertificateDetails(pemContent, remainingDays));
    setShowSigningCertDetailsPopup(true);
    setSigningCertTextValidated(true);

    if (source === 'file') {
      setSigningCertFile(file);
      setSigningCertText('');
    } else {
      setSigningCertFile(null);
    }

    return true;
  };

  const handleSigningCertUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedExt = ['cer', 'pem', 'crt'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowedExt.includes(ext)) {
      toast.error(t('Invalid file extension. Allowed: .cer, .pem, .crt'));
      return;
    }
    // Validate certificate format (PEM)
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = String(e.target.result || '');
      processSigningCertificateContent(content, { source: 'file', file, showToast: true });
    };
    reader.readAsText(file);
  };

  const handleSigningCertTextChange = (value) => {
    setSigningCertText(value);
    setSigningCertTextValidated(false);
    setSigningCertFile(null);
    setSigningCertDetails(null);
    setShowSigningCertDetailsPopup(false);
    setSigningCertDaysRemaining(null);
    if (!value.trim()) {
      setFieldErrors((p) => ({ ...p, signingCert: '' }));
    }
  };

  const handleDecodeSigningCertText = () => {
    const content = signingCertText.trim();
    if (!content) {
      const errorMsg = t('Please enter a certificate in PEM format.');
      setFieldErrors((p) => ({ ...p, signingCert: errorMsg }));
      toast.error(errorMsg);
      return;
    }
    processSigningCertificateContent(content, { source: 'text', showToast: true });
  };

  const handleSigningCertDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleSigningCertUpload({ target: { files: [file] } });
    }
  };

  const removeSigningCertFile = () => {
    setSigningCertFile(null);
    setSigningCertDetails(null);
    setShowSigningCertDetailsPopup(false);
    setSigningCertDaysRemaining(null);
    setSigningCertTextValidated(false);
    setFieldErrors((p) => ({ ...p, signingCert: '' }));
    if (signingCertInputRef.current) {
      signingCertInputRef.current.value = '';
    }
  };

  const validateBusinessJustificationValue = (value) => {
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) return t('Business Justification is required');
    if (trimmedValue.length < 10) return t('Business Justification must be at least 10 characters');
    if (trimmedValue.length > 500) return t('Business Justification must not exceed 500 characters');
    return '';
  };

  // Handle logout URL changes (multi)
  const handleLogoutUrlChange = (index, value) => {
    const newUrls = [...logoutUrls];
    newUrls[index] = value;
    setLogoutUrls(newUrls);
    const nextLogoutErrors = getUrlFieldErrorsByIndex(newUrls, 'Logout URL', oidcRedirectUrlValidationOptions);
    setFieldErrors((prev) => ({
      ...prev,
      logoutUrls: nextLogoutErrors,
      logoutUrl: '',
    }));
  };
  const addLogoutUrl = () => {
    if (logoutUrls.length < 3) {
      setLogoutUrls([...logoutUrls, '']);
    }
  };
  const removeLogoutUrl = (index) => {
    if (logoutUrls.length > 1) {
      const newUrls = logoutUrls.filter((_, i) => i !== index);
      setLogoutUrls(newUrls);
      const nextLogoutErrors = getUrlFieldErrorsByIndex(newUrls, 'Logout URL', oidcRedirectUrlValidationOptions);
      setFieldErrors((prev) => {
        return {
          ...prev,
          logoutUrls: nextLogoutErrors,
          logoutUrl: '',
        };
      });
    }
  };

  // Map environment display text to API format
  const getApiEnvironment = () => {
    const normalizedEnvironment = (businessInfo.environment || '').trim().toLowerCase();
    if (normalizedEnvironment === 'production' || normalizedEnvironment === 'prod') {
      return 'PROD';
    }
    return 'NONPROD';
  };

  const getAccessControlPayloadValues = (value) => {
    if (!Array.isArray(value)) return [];

    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (typeof item === 'number') return String(item);
        return item?.id || item?.value || item?.displayText || '';
      })
      .filter(Boolean);
  };

  // Validate IDX Attribute
  const validateIdxAttribute = async () => {
    const trimmedIdxValue = idxAttributeValue.trim();

    if (!trimmedIdxValue) {
      setFieldErrors((p) => ({ ...p, idxAttributeValue: t('Please enter IDX Attribute value') }));
      toast.error(t('Please enter IDX Attribute value'));
      return;
    }

    if (hasExistingIdxAttribute === 'yes' && !existingIdxAttributePattern.test(trimmedIdxValue)) {
      const msg = t('Existing IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + status');
      setFieldErrors((p) => ({ ...p, idxAttributeValue: msg }));
      toast.error(msg);
      return;
    }

    // Only validate short-name pattern for new IDX attributes
    if (hasExistingIdxAttribute === 'no' && !validators.idxAttributePattern.test(trimmedIdxValue)) {
      const msg = t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters');
      setFieldErrors((p) => ({ ...p, idxAttributeValue: msg }));
      toast.error(msg);
      return;
    }
    setFieldErrors((p) => ({ ...p, idxAttributeValue: '' }));
    setIdxValidationStatus('validating');
    
    try {
      // Get environment from businessInfo dropdown
      const apiEnvironment = getApiEnvironment();

      if (hasExistingIdxAttribute === 'yes') {
        const idxValue = trimmedIdxValue;
        const response = await axios.post(
          `${REACT_APP_API_ENDPOINT}sso/idx/validateAttribute`,
          {
            idxAttribute: idxValue.substring(0, 200),
            environment: apiEnvironment
          }
        );

        // Existing attribute: SUCCESS means it exists in IDX schema — valid
        if (response?.data?.status === 'SUCCESS') {
          setIdxValidationStatus('valid');
          setIdxAttributeStatus(idxValue);
          toast.success(response?.data?.message || t('IDX Attribute validated successfully'));
        } else {
          setIdxValidationStatus('invalid');
          const errorMsg = response?.data?.message || t('IDX Attribute not found in IDX schema');
          setFieldErrors((p) => ({ ...p, idxAttributeValue: errorMsg }));
          toast.error(errorMsg);
        }
      } else {
        // New IDX: validate BOTH status and roles attributes.
        const statusAttributeValue = `pepapp${trimmedIdxValue}status`;
        const roleAttributeValue = `pepapp${trimmedIdxValue}roles`;

        const [statusCheck, roleCheck] = await Promise.allSettled([
          axios.post(`${REACT_APP_API_ENDPOINT}sso/idx/validateAttribute`, {
            idxAttribute: statusAttributeValue.substring(0, 200),
            environment: apiEnvironment,
          }),
          axios.post(`${REACT_APP_API_ENDPOINT}sso/idx/validateAttribute`, {
            idxAttribute: roleAttributeValue.substring(0, 200),
            environment: apiEnvironment,
          }),
        ]);

        if (statusCheck.status === 'rejected') {
          setIdxValidationStatus('invalid');
          setRoleIdxValidationStatus('invalid');
          const errorMsg = statusCheck.reason?.response?.data?.message || t('Error validating IDX Attribute');
          setFieldErrors((p) => ({ ...p, idxAttributeValue: errorMsg }));
          toast.error(errorMsg);
          return;
        }

        if (roleCheck.status === 'rejected') {
          setIdxValidationStatus('invalid');
          setRoleIdxValidationStatus('invalid');
          const errorMsg = roleCheck.reason?.response?.data?.message || t('Error validating Role IDX Attribute');
          setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: errorMsg }));
          toast.error(errorMsg);
          return;
        }

        const statusResponse = statusCheck.value;
        const roleResponse = roleCheck.value;

        // If either exists already, block and show API response message.
        if (statusResponse?.data?.status === 'SUCCESS') {
          setIdxValidationStatus('invalid');
          setRoleIdxValidationStatus('invalid');
          const errorMsg = statusResponse?.data?.message || t('IDX Attribute already exists in IDX schema. Please use a different name or select "Yes" for existing attribute.');
          setFieldErrors((p) => ({ ...p, idxAttributeValue: errorMsg }));
          toast.error(errorMsg);
          return;
        }

        if (roleResponse?.data?.status === 'SUCCESS') {
          setIdxValidationStatus('invalid');
          setRoleIdxValidationStatus('invalid');
          const errorMsg = roleResponse?.data?.message || t('Role IDX Attribute already exists in IDX schema. Please use a different name or select "Yes" for existing attribute.');
          setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: errorMsg }));
          toast.error(errorMsg);
          return;
        }

        // Both are valid (do not exist yet).
        setRoleIdxAttributeValue(trimmedIdxValue);
        setIdxValidationStatus('valid');
        setRoleIdxValidationStatus('valid');
        setIdxAttributeStatus(statusAttributeValue);
        setFieldErrors((p) => ({ ...p, idxAttributeValue: '', roleIdxAttributeValue: '' }));
        setNewIdxValidatedType('idx');
        setShowNewIdxValidatedPopup(true);
      }
    } catch (error) {
      console.error("Error validating IDX attribute:", error);
      setIdxValidationStatus('invalid');
      if (hasExistingIdxAttribute === 'no') {
        setRoleIdxValidationStatus('invalid');
      }
      const errorMsg = error?.response?.data?.message || t('Error validating IDX Attribute');
      setFieldErrors((p) => ({ ...p, idxAttributeValue: errorMsg }));
      toast.error(errorMsg);
    }
  };

  // Validate Application Name (check for duplicates)
  const validateApplicationName = async () => {
    const isApplicationNameValid = await trigger('applicationName');
    if (!isApplicationNameValid) {
      setAppNameValidationStatus('invalid');
      return;
    }

    const appName = getValues('applicationName');
    if (!appName || !appName.trim()) {
      toast.error(t('Please enter an Application Name'));
      return;
    }
    
    // Verify application using app type rules from onboarding type
    const appTypeMap = {
      'saml': 'WEB_APPLICATION',
      'oidc': 'WEB_APPLICATION',
      'oidc-m2m': 'SERVICE_APPLICATION'
    };
    const appType = appTypeMap[selectedType] || 'WEB_APPLICATION';
    const environment = getApiEnvironment();
    
    setAppNameValidationStatus('validating');
    
    try {
      const response = await axios.post(
        `${REACT_APP_API_ENDPOINT}sso/validate-app`,
        { 
          applicationName: appName.trim(),
          environment,
          appType: appType
        }
      );

      const responseData = response?.data || {};
      const isValidAppName = responseData?.valid === true;
      const validationMessage = responseData?.message || t('Application name validation completed');

      if (isValidAppName) {
        setAppNameValidationStatus('valid');
        setFieldErrors((prev) => ({ ...prev, applicationName: '' }));
        toast.success(validationMessage);
      } else {
        setAppNameValidationStatus('invalid');
        setFieldErrors((prev) => ({ ...prev, applicationName: validationMessage }));
        toast.error(validationMessage);
      }
    } catch (error) {
      console.error("Error validating application name:", error);
      setAppNameValidationStatus('invalid');
      toast.error(error?.response?.data?.message || t('Failed to validate application name'));
    }
  };

  // Get IDX preview - always with prefix/suffix
  const getIdxPreview = () => {
    if (idxAttributeValue) {
      const trimmedIdxValue = idxAttributeValue.trim();
      return hasExistingIdxAttribute === 'yes'
        ? trimmedIdxValue
        : `${idxAttributePrefix}${trimmedIdxValue}status`;
    }
    return '';
  };

  // Add authorization role - alphanumeric, 4-64 chars, allows _ - ( ), max 10 roles
  const addAuthorizationRole = () => {
    const trimmed = newRoleName.trim();
    if (!trimmed) {
      setFieldErrors((p) => ({ ...p, newRoleName: t('Role name is required') }));
      return;
    }
    if (authorizationRoles.length >= 10) {
      setFieldErrors((p) => ({ ...p, newRoleName: t('Maximum 10 roles can be added') }));
      return;
    }
    if (!validators.rolePattern.test(trimmed)) {
      setFieldErrors((p) => ({ ...p, newRoleName: t('Role name must be 4-64 alphanumeric characters; allowed special chars: _ - ( )') }));
      return;
    }
    if (authorizationRoles.some((role) => role?.name?.toLowerCase?.() === trimmed.toLowerCase())) {
      setFieldErrors((p) => ({ ...p, newRoleName: t('Role already added') }));
      return;
    }
    setAuthorizationRoles([...authorizationRoles, { name: trimmed }]);
    setNewRoleName('');
    setFieldErrors((p) => ({ ...p, newRoleName: '' }));
  };

  // Remove authorization role
  const removeAuthorizationRole = (role) => {
    setAuthorizationRoles(authorizationRoles.filter((item) => item?.name !== role));
  };

  // Get Role IDX preview
  const getRoleIdxPreview = () => {
    const derivedRoleIdxValue = getDerivedRoleIdxInputValue();
    if (derivedRoleIdxValue) {
      const trimmedRoleIdxValue = derivedRoleIdxValue.trim();
      return hasExistingIdxForRole === 'yes'
        ? trimmedRoleIdxValue
        : formatRoleIdxWithPrefixAndSuffix(trimmedRoleIdxValue);
    }
    return '';
  };

  const getRoleIdxNoCaseText = () => {
    const derivedRoleIdxValue = getDerivedRoleIdxInputValue();
    if (!derivedRoleIdxValue) {
      return '';
    }
    return formatRoleIdxWithPrefixAndSuffix(derivedRoleIdxValue);
  };

  // Validate Role IDX Attribute
  const validateRoleIdxAttribute = async ({ showPopupOnNew = true } = {}) => {
    const trimmedRoleIdxValue = getDerivedRoleIdxInputValue().trim();

    if (!trimmedRoleIdxValue) {
      setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: t('Please enter IDX Attribute value') }));
      toast.error(t('Please enter IDX Attribute value'));
      return;
    }

    if (hasExistingIdxForRole === 'yes' && !existingRoleIdxAttributePattern.test(trimmedRoleIdxValue)) {
      const msg = t('Existing Role IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + roles');
      setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: msg }));
      toast.error(msg);
      return;
    }

    // Only validate short-name pattern for new Role IDX attributes
    if (hasExistingIdxForRole === 'no' && !validators.idxAttributePattern.test(trimmedRoleIdxValue)) {
      const msg = t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters');
      setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: msg }));
      toast.error(msg);
      return;
    }
    setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: '' }));
    setRoleIdxValidationStatus('validating');

    try {
      const idxValue = hasExistingIdxForRole === 'yes'
        ? trimmedRoleIdxValue
        : `${idxAttributePrefix}${trimmedRoleIdxValue}roles`;
      const apiEnvironment = getApiEnvironment();

      const response = await axios.post(
        `${REACT_APP_API_ENDPOINT}sso/idx/validateAttribute`,
        {
          idxAttribute: idxValue.substring(0, 200),
          environment: apiEnvironment
        }
      );

      if (hasExistingIdxForRole === 'yes') {
        // Existing attribute: SUCCESS means it exists in IDX schema — valid
        if (response?.data?.status === 'SUCCESS') {
          setRoleIdxValidationStatus('valid');
          toast.success(response?.data?.message || t('Role IDX Attribute validated successfully'));
        } else {
          setRoleIdxValidationStatus('invalid');
          const errorMsg = response?.data?.message || t('Role IDX Attribute not found in IDX schema');
          setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: errorMsg }));
          toast.error(errorMsg);
        }
      } else {
        // New attribute: SUCCESS means it already exists — invalid (should not exist)
        if (response?.data?.status === 'SUCCESS') {
          setRoleIdxValidationStatus('invalid');
          const errorMsg = t('Role IDX Attribute already exists in IDX schema. Please use a different name or select "Yes" for existing attribute.');
          setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: errorMsg }));
          toast.error(errorMsg);
        } else {
          // Not found — valid for new attribute; popup only for explicit validation flows
          setRoleIdxValidationStatus('valid');
          setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: '' }));
          if (showPopupOnNew) {
            setNewIdxValidatedType('role');
            setShowNewIdxValidatedPopup(true);
          }
        }
      }
    } catch (error) {
      console.error("Error validating Role IDX attribute:", error);
      if (hasExistingIdxForRole === 'no') {
        setRoleIdxValidationStatus('invalid');
        const errorMsg = error?.response?.data?.message || t('Error validating Role IDX Attribute');
        setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: errorMsg }));
        toast.error(errorMsg);
      } else {
        setRoleIdxValidationStatus('invalid');
        const errorMsg = error?.response?.data?.message || t('Error validating Role IDX Attribute');
        setFieldErrors((p) => ({ ...p, roleIdxAttributeValue: errorMsg }));
        toast.error(errorMsg);
      }
    }
  };

  // Add custom scope for M2M - max 5, no quotes/spaces/forward-backward slashes
  const addCustomScope = () => {
    const trimmed = newScopeName.trim();
    if (!trimmed) {
      setFieldErrors((p) => ({ ...p, newScopeName: t('Scope name is required') }));
      return;
    }
    if (customScopes.length >= 5) {
      setFieldErrors((p) => ({ ...p, newScopeName: t('Maximum 5 custom scopes can be added') }));
      return;
    }
    if (!validators.scopePattern.test(trimmed)) {
      setFieldErrors((p) => ({ ...p, newScopeName: t('Scope name cannot contain quotes, spaces, forward (/) or backward (\\) slashes') }));
      return;
    }
    if (trimmed.length > 100) {
      setFieldErrors((p) => ({ ...p, newScopeName: t('Scope name must not exceed 100 characters') }));
      return;
    }
    if (customScopes.includes(trimmed)) {
      setFieldErrors((p) => ({ ...p, newScopeName: t('Scope already added') }));
      return;
    }
    setCustomScopes([...customScopes, trimmed]);
    setNewScopeName('');
    setFieldErrors((p) => ({ ...p, newScopeName: '' }));
  };

  // Remove custom scope
  const removeCustomScope = (scope) => {
    setCustomScopes(customScopes.filter(s => s !== scope));
  };

  // Application/Service Type options for M2M
  const applicationServiceTypeOptions = [
    { id: "api", displayText: "API" },
    { id: "microservice", displayText: "Microservice" },
    { id: "backend", displayText: "Backend Service" },
    { id: "integration", displayText: "Integration Service" },
  ];

  // SSO Type configurations
  const ssoTypeConfig = {
    saml: {
      number: 1,
      title: "SAML 2.0",
      description: "Traditional enterprise SSO protocol for web applications with robust security features.",
      color: "#0066cc"
    },
    oidc: {
      number: 2,
      title: "OIDC - OpenID Connect",
      description: "Modern authentication protocol built on OAuth 2.0, ideal for web and mobile apps.",
      color: "#2e7d32"
    },
    "oidc-m2m": {
      number: 3,
      title: "OIDC - Machine2Machine(API)",
      description: "Secure authentication for server-to-server API communication without user interaction.",
      color: "#0066cc"
    }
  };

  const currentSsoType = ssoTypeConfig[selectedType] || ssoTypeConfig.saml;

  // Validate non-RHF fields (IDX, Logo, URLs, etc.)
  const validateNonRHFFields = () => {
    const errs = { loginUrls: {}, logoutUrls: {}, logoutUrl: '', idxAttributeValue: '', roleIdxAttributeValue: '', newRoleName: '', newScopeName: '', applicationName: '', businessJustification: '', signingCert: '' };
    let hasError = false;

    // Validate Application Name is validated (required for all SSO types)
    if (appNameValidationStatus !== 'valid') {
      errs.applicationName = t('Please validate the Application Name before submitting');
      hasError = true;
      toast.error(t('Please validate the Application Name before submitting'));
    }

    // OIDC: validate login URLs
    if (selectedType === 'oidc') {
      const validLoginUrls = loginUrls.filter((u) => u && u.trim());
      if (validLoginUrls.length === 0) {
        errs.loginUrls[0] = t('At least one Login URL is required');
        hasError = true;
      } else {
        loginUrls.forEach((u, idx) => {
          if (u.trim()) {
            const uerr = validateUrl(u, 'Login URL', oidcRedirectUrlValidationOptions);
            if (uerr) {
              errs.loginUrls[idx] = uerr;
              hasError = true;
            }
          }
        });

        const loginDuplicateErrors = getDuplicateUrlErrors(loginUrls, 'Login URL', oidcRedirectUrlValidationOptions);
        if (Object.keys(loginDuplicateErrors).length > 0) {
          Object.assign(errs.loginUrls, loginDuplicateErrors);
          hasError = true;
        }
      }
      // Logout URL validation
      const validLogoutUrls = logoutUrls.filter((u) => u && u.trim());
      if (validLogoutUrls.length === 0) {
        errs.logoutUrl = t('At least one Logout URL is required');
        hasError = true;
      } else {
        logoutUrls.forEach((u, idx) => {
          if (u.trim()) {
            const uerr = validateUrl(u, 'Logout URL', oidcRedirectUrlValidationOptions);
            if (uerr) {
              errs.logoutUrls[idx] = uerr;
              hasError = true;
            }
          }
        });

        const logoutDuplicateErrors = getDuplicateUrlErrors(logoutUrls, 'Logout URL', oidcRedirectUrlValidationOptions);
        if (Object.keys(logoutDuplicateErrors).length > 0) {
          Object.assign(errs.logoutUrls, logoutDuplicateErrors);
          hasError = true;
        }

        if (Object.keys(errs.logoutUrls).length > 0) {
          errs.logoutUrl = errs.logoutUrl || t('Please correct invalid Logout URL entries');
          hasError = true;
        }
      }
      // OIDC: validate authorization roles and role IDX attribute
      if (needAuthorizationRoles === 'yes') {
        if (authorizationRoles.length === 0) {
          errs.newRoleName = t('At least one Authorization Role is required (Min 1, Max 10)');
          hasError = true;
        }
        const derivedRoleIdxValue = getDerivedRoleIdxInputValue();
        if (!derivedRoleIdxValue) {
          errs.roleIdxAttributeValue = t('Role IDX Attribute is required');
          hasError = true;
        } else if (hasExistingIdxForRole === 'yes') {
          if (!existingRoleIdxAttributePattern.test(derivedRoleIdxValue.trim())) {
            errs.roleIdxAttributeValue = t('Existing Role IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + roles');
            hasError = true;
          }
        } else if (!validators.idxAttributePattern.test(derivedRoleIdxValue.trim())) {
          errs.roleIdxAttributeValue = t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters');
          hasError = true;
        } else if (roleIdxValidationStatus !== 'valid') {
          errs.roleIdxAttributeValue = t('Please validate the Role IDX Attribute before submitting');
          hasError = true;
        }
      }
    }

    // OIDC/SAML: validate IDX attribute if needed
    if ((selectedType === 'oidc' || selectedType === 'saml') && needIdxAttribute === 'yes') {
      if (!idxAttributeValue) {
        errs.idxAttributeValue = t('IDX Attribute is required');
        hasError = true;
      } else if (hasExistingIdxAttribute === 'yes') {
        if (!existingIdxAttributePattern.test(idxAttributeValue.trim())) {
          errs.idxAttributeValue = t('Existing IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + status');
          hasError = true;
        }
      } else if (!validators.idxAttributePattern.test(idxAttributeValue.trim())) {
        errs.idxAttributeValue = t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters');
        hasError = true;
      } else if (idxValidationStatus !== 'valid') {
        errs.idxAttributeValue = t('Please validate the IDX Attribute before submitting');
        hasError = true;
      }
    }

    // SAML: validate authorization roles and role IDX attribute
    if (selectedType === 'saml' && needAuthorizationRoles === 'yes') {
      if (authorizationRoles.length === 0) {
        errs.newRoleName = t('At least one Authorization Role is required (Min 1, Max 10)');
        hasError = true;
      }
      const derivedRoleIdxValue = getDerivedRoleIdxInputValue();
      if (!derivedRoleIdxValue) {
        errs.roleIdxAttributeValue = t('Role IDX Attribute is required');
        hasError = true;
      } else if (hasExistingIdxForRole === 'yes') {
        if (!existingRoleIdxAttributePattern.test(derivedRoleIdxValue.trim())) {
          errs.roleIdxAttributeValue = t('Existing Role IDX Attribute must follow format pepapp + 2 to 13 alphanumeric characters + roles');
          hasError = true;
        }
      } else if (!validators.idxAttributePattern.test(derivedRoleIdxValue.trim())) {
        errs.roleIdxAttributeValue = t('IDX Attribute Name must consist of alphanumeric characters and have a length between 2 and 8 characters');
        hasError = true;
      } else if (roleIdxValidationStatus !== 'valid') {
        errs.roleIdxAttributeValue = t('Please validate the Role IDX Attribute before submitting');
        hasError = true;
      }
    }

    // Logo validation
    if ((selectedType === 'oidc' || selectedType === 'saml') && hasOwnLogo === 'yes' && !applicationLogo) {
      hasError = true;
    }

    // M2M: validate custom scopes
    if (selectedType === 'oidc-m2m' && m2mEnvironment === 'private' && customScopes.length === 0) {
      hasError = true;
    }

    // SAML: Signing Certificate validation
    if (selectedType === 'saml' && isRestrictedOrConfidential) {
      const hasSigningCertInput = !!signingCertFile || !!signingCertText.trim();
      if (hasSigningCert === 'yes' && !hasSigningCertInput) {
        errs.signingCert = t('Signing Certificate is required');
        hasError = true;
      } else if (hasSigningCert === 'yes' && signingCertEntryMode === 'text' && !signingCertTextValidated) {
        errs.signingCert = t('Please validate the Signing Certificate before submitting');
        hasError = true;
      } else if (hasSigningCert === 'yes' && (signingCertDaysRemaining === null || signingCertDaysRemaining < MIN_CERT_VALIDITY_DAYS)) {
        errs.signingCert = t('Signing Certificate validity must be at least 30 days from today.');
        hasError = true;
      }
      if (hasSigningCert === 'no') {
        const businessJustificationError = validateBusinessJustificationValue(businessJustification);
        if (businessJustificationError) {
          errs.businessJustification = businessJustificationError;
          hasError = true;
        }
      }
    }

    setFieldErrors(errs);
    return { hasError, errs };
  };

  // Handle form validation errors from react-hook-form
  const onFormError = (errors) => {
    console.log("Form validation errors:", errors);
    // Also validate non-RHF fields to show all errors
    validateNonRHFFields();
    // Expand all sections so user can see all errors
    setIsAppDetailsExpanded(true);
    setIsTechnicalConfigExpanded(true);
    setIsAdditionalDetailsExpanded(true);
    setIsAccessControlExpanded(true);
    setIsElevatedPermissionsExpanded(true);
    setIsCertificationDetailsExpanded(true);
    setIsAppLogoExpanded(true);
    toast.error(t('Please fix the validation errors before submitting'));
  };

  const onSubmit = (data) => {
    console.log("onSubmit called with data:", data);
    console.log("selectedType:", selectedType);
    console.log("businessInfo:", businessInfo);
    
    // Validate non-RHF fields before submission
    const { hasError } = validateNonRHFFields();

    if (hasError) {
      // Expand all sections so user can see all errors
      setIsAppDetailsExpanded(true);
      setIsTechnicalConfigExpanded(true);
      setIsAdditionalDetailsExpanded(true);
      setIsAccessControlExpanded(true);
      setIsElevatedPermissionsExpanded(true);
      setIsCertificationDetailsExpanded(true);
      setIsAppLogoExpanded(true);
      toast.error(t('Please fix the validation errors before submitting'));
      return;
    }

    // Build the API payload based on SSO type
    const buildPayload = () => {
      const normalizeUrlForPayload = (urlValue, opts = {}) => normalizeUrlWithDefaultProtocol(urlValue, opts);

      const basePayload = {
        comments: data.comments || '',
      };

      // Map environment to API format
      const envMap = {
        'Non-Production': 'NONPROD',
        'Production': 'PROD',
      };
      const apiEnvironment = envMap[businessInfo.environment] || 'NONPROD';

      // Map data classification to API format
      const dataClassMap = {
        'PEPSICO INTERNAL': 'INTERNAL',
        'PEPSICO CONFIDENTIAL': 'CONFIDENTIAL',
        'PEPSICO PUBLIC': 'PUBLIC',
        'PEPSICO RESTRICTED': 'RESTRICTED',
      };
      const apiDataClassification = dataClassMap[businessInfo.dataClassification] || 'INTERNAL';

      if (selectedType === 'saml') {
        // Map Name ID Format to API format
        const nameIdFormatMap = {
          'Unspecified': 'Unspecified',
          'EmailAddress': 'EmailAddress',
          'Persistent': 'Persistent',
          // 'Transient': 'Transient',
        };
        const selectedNameIdFormat = data.nameIdFormat?.[0]?.id || 'Unspecified';
        const apiNameIdFormat = nameIdFormatMap[selectedNameIdFormat] || 'Unspecified';

        return {
          ...basePayload,
          type: 'SAML',
          certBusinessJustification: hasSigningCert === 'no' ? businessJustification : '',
          request: {
            // Business Information
            sector: businessInfo.sector || '',
            environment: apiEnvironment,
            businessAppName: businessInfo.businessApplication || '',
            applicationService: businessInfo.applicationService || '',
            dataClassification: apiDataClassification,
            applicationOwner: businessInfo.applicationOwner?.split(' - ')[0] || '',
            // Application Details
            applicationName: data.applicationName,
            applicationURL: normalizeUrlForPayload(data.applicationUrl, applicationUrlValidationOptions),
            acsURL: data.assertionConsumerServiceUrl,
            entityID: data.audienceUri,
            nameIdFormat: apiNameIdFormat,
            // Access Control
            applicationAccessControl: getAccessControlPayloadValues(data.applicationAccessControl),
            needIdxAttribute: needIdxAttribute === 'yes',
            hasExistingIdxAttribute: hasExistingIdxAttribute === 'yes',
            newIdxAttribute: hasExistingIdxAttribute === 'no',
            idxAttribute: needIdxAttribute === 'yes' ? getIdxAttributePayloadValue() : null,
            // Authorization Roles
            needAuthorizationRoles: needAuthorizationRoles === 'yes',
            roles: needAuthorizationRoles === 'yes' ? authorizationRoles.map(r => r.name) : [],
            hasExistingIdxForRole: hasExistingIdxForRole === 'yes',
            newRoleIdxAttribute: hasExistingIdxForRole === 'no',
            roleIdxAttribute: needAuthorizationRoles === 'yes' ? getDerivedRoleIdxPayloadValue() : null,
            // Elevated Permissions
            isMFARequired: isAdminApp === 'yes',
            // Logo
            hasLogo: hasOwnLogo === 'yes',
            // Signing Certificate (only for Confidential/Restricted)
            hasSigningCertificate: isRestrictedOrConfidential ? hasSigningCert === 'yes' : false,
            // Acknowledgment
            disclaimerAccepted: acknowledgeChecked,
          },
        };
      } else if (selectedType === 'oidc') {
        // Map application type
        const appTypeMap = {
          'Single Page Application': 'SINGLE_PAGE_APPLICATION',
          'Web Application': 'WEB_APPLICATION',
          'Native Application': 'NATIVE_APPLICATION',
        };
        const apiAppType = appTypeMap[data.applicationType?.[0]?.displayText] || 'WEB_APPLICATION';

        // Build grants array based on application type
        const grants = ['AUTHORIZATION_CODE', 'REFRESH_TOKEN'];

        return {
          ...basePayload,
          type: 'OIDC',
          request: {
            // Business Information
            sector: businessInfo.sector || '',
            environment: apiEnvironment,
            businessAppName: businessInfo.businessApplication || '',
            applicationService: businessInfo.applicationService || '',
            dataClassification: apiDataClassification,
            applicationOwner: businessInfo.applicationOwner?.split(' - ')[0] || '',
            // Application Details
            applicationName: data.applicationName,
            applicationURL: normalizeUrlForPayload(data.applicationUrl, applicationUrlValidationOptions),
            applicationType: apiAppType,
            applicationTypeName: data.applicationType?.[0]?.displayText || 'Web Application',
            // Login/Logout URLs
            signinUris: loginUrls
              .filter(u => u && u.trim())
              .map((urlValue) => normalizeUrlForPayload(urlValue, oidcRedirectUrlValidationOptions)),
            signoutUris: logoutUrls
              .filter(u => u && u.trim())
              .map((urlValue) => normalizeUrlForPayload(urlValue, oidcRedirectUrlValidationOptions)),
            // Grants
            grants: grants,
            // Access Control
            applicationAccessControl: getAccessControlPayloadValues(data.applicationAccessControl),
            needIdxAttribute: needIdxAttribute === 'yes',
            hasExistingIdxAttribute: hasExistingIdxAttribute === 'yes',
            newIdxAttribute: hasExistingIdxAttribute === 'no',
            idxAttribute: needIdxAttribute === 'yes' ? getIdxAttributePayloadValue() : null,
            // Authorization Roles
            needAuthorizationRoles: needAuthorizationRoles === 'yes',
            roles: needAuthorizationRoles === 'yes' ? authorizationRoles.map(r => r.name) : [],
            hasExistingIdxForRole: hasExistingIdxForRole === 'yes',
            newRoleIdxAttribute: hasExistingIdxForRole === 'no',
            roleIdxAttribute: needAuthorizationRoles === 'yes' ? getDerivedRoleIdxPayloadValue() : null,
            // Elevated Permissions
            isMFARequired: isAdminApp === 'yes',
            // Logo
            hasLogo: hasOwnLogo === 'yes',
            // Acknowledgment
            disclaimerAccepted: acknowledgeChecked,
          },
        };
      } else if (selectedType === 'oidc-m2m') {
        return {
          ...basePayload,
          type: 'M2M',
          request: {
            // Business Information
            sector: businessInfo.sector || '',
            environment: apiEnvironment,
            businessAppName: businessInfo.businessApplication || '',
            applicationService: businessInfo.applicationService || '',
            dataClassification: apiDataClassification,
            applicationOwner: businessInfo.applicationOwner?.split(' - ')[0] || '',
            // Application Details
            applicationName: data.applicationName,
            // Application/Services Type
            accessType: m2mEnvironment === 'private' ? 'PRIVATE' : 'PUBLIC',
            accessTypeName: m2mEnvironment === 'private' ? 'Private' : 'Public',
            // Custom Scopes (only for Private)
            scopes: m2mEnvironment === 'private' ? customScopes : [],
            // Grants
            grants: ['CLIENT_CREDENTIALS'],
            // Type
            type: 'SERVICE_APPLICATION',
            // Acknowledgment
            disclaimerAccepted: acknowledgeChecked,
          },
        };
      }

      return basePayload;
    };

    const submitToApi = async () => {
      showLoader();
      try {
        const payload = buildPayload();
        console.log("Submitting payload:", payload);

        // Create FormData for multipart/form-data request
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }));

        // Append files if present
        if (selectedType === 'saml') {
          if (hasSigningCert === 'yes' && signingCertFile) {
            formData.append('certificate', signingCertFile);
          } else if (hasSigningCert === 'yes' && signingCertText.trim()) {
            formData.append(
              'certificate',
              new Blob([signingCertText.trim()], { type: 'application/x-pem-file' }),
              'certificate.pem'
            );
          }
          if (hasOwnLogo === 'yes' && applicationLogo) {
            formData.append('logo', applicationLogo);
          }
        } else if (selectedType === 'oidc') {
          if (hasOwnLogo === 'yes' && applicationLogo) {
            formData.append('logo', applicationLogo);
          }
        }

        const response = await axios.post(
          `${REACT_APP_API_ENDPOINT}sso/submit`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        console.log("API Response:", response.data);

        // Check if response indicates an error (API may return 200 with error status in body)
        const responseStatus = response.data?.status?.toLowerCase();
        if (responseStatus === 'error' || responseStatus === 'failure' || responseStatus === 'failed' || response.data?.error || response.data?.errorMessage) {
          const errorMessage = response.data?.errorMessage || response.data?.message || response.data?.error || t('Failed to submit SSO request');
          toast.error(errorMessage, { autoClose: 5000 });
          hideLoader();
          return;
        }

        // Store submitted data for modal display
        setSubmittedData({
          ssoType: currentSsoType.title,
          sector: businessInfo.sector,
          businessApplication: businessInfo.businessApplication,
          dataClassification: businessInfo.dataClassification ,
          environment: businessInfo.environment,
          applicationService: businessInfo.applicationService,
          applicationOwner: businessInfo.applicationOwner,
          requestNumber: response.data?.requestNumber || payload.request?.cmpRequestNumber || payload.request?.requestNumber,
        });
        setShowSuccessModal(true);
        toast.success(t('SSO request submitted successfully'));
      } catch (error) {
        console.error("Submit error:", error);
        console.log("Error response data:", error.response?.data);
        const errorMsg = error.response?.data?.errorMessage || error.response?.data?.message || error.message || t('Failed to submit SSO request');
        toast.error(errorMsg, { autoClose: 5000 });
      } finally {
        hideLoader();
      }
    };

    submitToApi();
  };

  const handleViewRequests = () => {
    setShowSuccessModal(false);
    const statePayload = { type: "requestNumber", value: submittedData?.requestNumber };
    navigate('/audit-requests', { state: statePayload });
  };

  const handleBackFromModal = () => {
    setShowSuccessModal(false);
    navigate('/sso-onboarding');
  };

  const handleBack = () => {
    // Pass back the business info and selected type to preserve user inputs
    navigate("/sso-onboarding", {
      state: {
        businessInfo,
        selectedType,
        isEditing: true,
      },
    });
  };

  const handleChange = () => {
    // Pass back the business info and selected type to preserve user inputs
    navigate("/sso-onboarding", {
      state: {
        businessInfo,
        selectedType,
        isEditing: true,
      },
    });
  };

  const handleCancel = () => {
    navigate("/sso-onboarding");
  };

  if (!hasValidEntryState) {
    return null;
  }

  return (
    <div className="sso-details-container">
      {/* Header */}
      <div className="sso-details-header">
        <div className="sso-details-header-left">
          <button className="sso-back-button" onClick={handleBack} type="button">
            <Icon icon="arrow_back" size="medium" />
          </button>
          <div className="sso-details-title-section">
            <h1 className="sso-details-title">{t('SSO Onboarding')}</h1>
            <p className="sso-details-subtitle">
              {t('Self-service portal that empowers application team to seamlessly initiate onboarding and manage PepsiCo SSO enabled applications.')}
            </p>
          </div>
        </div>
      </div>

      {/* SSO Type Banner */}
      <div className="sso-type-banner">
        <div className="sso-type-banner-left">
          <div 
            className="sso-type-number" 
            style={{ backgroundColor: currentSsoType.color }}
          >
            {currentSsoType.number}
          </div>
          <div className="sso-type-info">
            <h3 className="sso-type-title">{currentSsoType.title}</h3>
            <p className="sso-type-desc">{currentSsoType.description}</p>
          </div>
        </div>
        <button className="sso-change-btn" onClick={handleChange}>
          <Icon icon="edit" size="small" />
          {t('Change')}
        </button>
      </div>

      <div className={`sso-details-content ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
        {/* Left Section */}
        <div className="sso-details-main">
          {/* Business Information Card */}
          <div className="sso-details-card">
            <div className="sso-details-card-header">
              <h2 className="sso-details-card-title">{t('Business Information')}</h2>
              <div className="sso-details-card-actions">
                {/* <span className="sso-required-indicator"></span> */}
                <Icon 
                  icon="keyboard_arrow_up" 
                  size="medium" 
                />
              </div>
            </div>

            {isBusinessInfoExpanded && (
              <div className="sso-business-info-grid">
                <div className="sso-info-item">
                  <span className="sso-info-label">{t('Sector')}:</span>
                  <span className="sso-info-value">{businessInfo.sector || "CGF"}</span>
                </div>
                <div className="sso-info-item">
                  <span className="sso-info-label">{t('Environment')}:</span>
                  <span className="sso-info-value">{businessInfo.environment || "Pre-Production"}</span>
                </div>
                <div className="sso-info-item">
                  <span className="sso-info-label">{t('Business Application')}:</span>
                  <span className="sso-info-value">{businessInfo.businessApplication || "IAM CENTRAL"}</span>
                </div>
                <div className="sso-info-item">
                  <span className="sso-info-label">{t('Application Service')}:</span>
                  <span className="sso-info-value">{businessInfo.applicationService || "IAM CENTRAL"}</span>
                </div>
                <div className="sso-info-item">
                  <span className="sso-info-label">{t('Data Classification')}:</span>
                  <span className="sso-info-value">{businessInfo.dataClassification || "PEPSICO INTERNAL"}</span>
                </div>
                <div className="sso-info-item">
                  <span className="sso-info-label">{t('Application Owner')}:</span>
                  <span className="sso-info-value">{businessInfo.applicationOwner}</span>
                </div>
              </div>
            )}
          </div>

          {/* Application Details Card */}
          <div className="sso-details-card">
            <div 
              className="sso-details-card-header clickable"
              onClick={() => setIsAppDetailsExpanded(!isAppDetailsExpanded)}
            >
              <h2 className="sso-details-card-title">{t('Application Details')}</h2>
              <Icon 
                icon={isAppDetailsExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                size="medium" 
              />
            </div>

            {isAppDetailsExpanded && (
              <form onSubmit={handleSubmit(onSubmit, onFormError)}>
                {/* SAML Form Fields */}
                {selectedType === 'saml' && (
                  <>
                    {/* Row 1: Application Name & End-user Application URL */}
                    <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                      <div 
                        className="sso-form-field sso-field-with-tooltip"
                        onMouseEnter={() => setFocusedField('applicationName')}
                        onMouseLeave={() => focusedField === 'applicationName' && setFocusedField(null)}
                      >
                        <label className="sso-label">
                          <span className="label-text">{t('Application Name')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="applicationName"
                          control={control}
                          rules={validationRules.applicationName}
                          render={({ field }) => (
                            <div className="sso-input-with-validate">
                              <input
                                {...field}
                                type="text"
                                className="sso-input"
                                placeholder={t('Type...')}
                                maxLength={30}
                                onFocus={() => setFocusedField('applicationName')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setAppNameValidationStatus(null);
                                  setTimeout(checkAppDetailsCompletion, 100);
                                }}
                              />
                              <button
                                type="button"
                                className={`sso-inline-validate-btn ${appNameValidationStatus === 'valid' ? 'valid' : ''}`}
                                onClick={validateApplicationName}
                                disabled={isApplicationNameValidateDisabled}
                              >
                                {appNameValidationStatus === 'validating' ? 'Validating...' : appNameValidationStatus === 'valid' ? <><Icon icon="check_circle" size="small" /></> : 'Validate'}
                              </button>
                            </div>
                          )}
                        />
                        {focusedField === 'applicationName' && (
                          <div className="sso-input-tooltip">{tooltipMessages.applicationName}</div>
                        )}
                        {errors.applicationName && (
                          <span className="error-text">{errors.applicationName.message}</span>
                        )}
                        {fieldErrors.applicationName && !errors.applicationName && (
                          <span className="error-text">{fieldErrors.applicationName}</span>
                        )}
                      </div>

                      <div 
                        className="sso-form-field sso-field-with-tooltip"
                        onMouseEnter={() => setFocusedField('applicationUrl')}
                        onMouseLeave={() => focusedField === 'applicationUrl' && setFocusedField(null)}
                      >
                        <label className="sso-label">
                          <span className="label-text">{t('End-user Application URL')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="applicationUrl"
                          control={control}
                          rules={validationRules.applicationUrl}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              className="sso-input"
                              placeholder="https://example.com"
                              onFocus={() => setFocusedField('applicationUrl')}
                              onBlur={() => setFocusedField(null)}
                              onChange={(e) => {
                                field.onChange(e);
                                trigger('assertionConsumerServiceUrl');
                                setTimeout(checkAppDetailsCompletion, 100);
                              }}
                            />
                          )}
                        />
                        {focusedField === 'applicationUrl' && (
                          <div className="sso-input-tooltip">{tooltipMessages.applicationUrl}</div>
                        )}
                        {errors.applicationUrl && (
                          <span className="error-text">{errors.applicationUrl.message}</span>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Assertion Consumer Service URL & Audience URI */}
                    <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                      <div 
                        className="sso-form-field sso-field-with-tooltip"
                        onMouseEnter={() => setFocusedField('assertionConsumerServiceUrl')}
                        onMouseLeave={() => focusedField === 'assertionConsumerServiceUrl' && setFocusedField(null)}
                      >
                        <label className="sso-label">
                          <span className="label-text">{t('Assertion Consumer Service (ACS) URL')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="assertionConsumerServiceUrl"
                          control={control}
                          rules={validationRules.assertionConsumerServiceUrl}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              className="sso-input"
                              placeholder="https://example.com/acs"
                              onChange={(e) => {
                                field.onChange(e);
                                trigger('applicationUrl');
                                setTimeout(checkAppDetailsCompletion, 100);
                              }}
                              onFocus={() => setFocusedField('assertionConsumerServiceUrl')}
                              onBlur={() => setFocusedField(null)}
                            />
                          )}
                        />
                        {focusedField === 'assertionConsumerServiceUrl' && (
                          <div className="sso-input-tooltip">{tooltipMessages.assertionConsumerServiceUrl}</div>
                        )}
                        {errors.assertionConsumerServiceUrl && (
                          <span className="error-text">{errors.assertionConsumerServiceUrl.message}</span>
                        )}
                      </div>

                      <div 
                        className="sso-form-field sso-field-with-tooltip"
                        onMouseEnter={() => setFocusedField('audienceUri')}
                        onMouseLeave={() => focusedField === 'audienceUri' && setFocusedField(null)}
                      >
                        <label className="sso-label">
                          <span className="label-text">{t('Audience URI (SP Entity ID)')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="audienceUri"
                          control={control}
                          rules={validationRules.audienceUri}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              className="sso-input"
                              placeholder={t('Type...')}
                              onChange={(e) => {
                                field.onChange(e);
                                setTimeout(checkAppDetailsCompletion, 100);
                              }}
                              onFocus={() => setFocusedField('audienceUri')}
                              onBlur={() => setFocusedField(null)}
                            />
                          )}
                        />
                        {focusedField === 'audienceUri' && (
                          <div className="sso-input-tooltip">{tooltipMessages.audienceUri}</div>
                        )}
                        {errors.audienceUri && (
                          <span className="error-text">{errors.audienceUri.message}</span>
                        )}
                      </div>
                    </div>

                    {/* Row 3: Name ID Format */}
                    <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                      <div 
                        className="sso-form-field sso-field-with-tooltip"
                        onMouseEnter={() => setFocusedField('nameIdFormat')}
                        onMouseLeave={() => setFocusedField(null)}
                      >
                        <label className="sso-label">
                          <span className="label-text">{t('Name ID Format')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="nameIdFormat"
                          control={control}
                          rules={validationRules.nameIdFormat}
                          render={({ field }) => (
                            <Dropdown
                              placeholder={t('Select Name ID Format')}
                              childList={nameIdFormatOptions}
                              selectedValue={field.value}
                              setSelectedValue={(val) => {
                                field.onChange(val);
                                setTimeout(checkAppDetailsCompletion, 100);
                              }}
                              size="medium"
                              selection="single"
                            />
                          )}
                        />
                        {focusedField === 'nameIdFormat' && (
                          <div className="sso-input-tooltip">{tooltipMessages.nameIdFormat}</div>
                        )}
                        {errors.nameIdFormat && (
                          <span className="error-text">{errors.nameIdFormat.message}</span>
                        )}
                      </div>
                    </div>

                  </>
                )}

                {/* OIDC-M2M Form Fields */}
                {selectedType === 'oidc-m2m' && (
                  <>
                    {/* Row 1: Application Name & Application/Services Type */}
                    <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                      <div className="sso-form-field sso-field-with-tooltip">
                        <label className="sso-label">
                          <span className="label-text">{t('Application Name')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="applicationName"
                          control={control}
                          rules={validationRules.applicationName}
                          render={({ field }) => (
                            <div className="sso-input-with-validate">
                              <input
                                {...field}
                                type="text"
                                className="sso-input"
                                placeholder={t('Type...')}
                                maxLength={30}
                                onFocus={() => setFocusedField('applicationName')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setAppNameValidationStatus(null);
                                  setTimeout(checkAppDetailsCompletion, 100);
                                }}
                              />
                              <button
                                type="button"
                                className={`sso-inline-validate-btn ${appNameValidationStatus === 'valid' ? 'valid' : ''}`}
                                onClick={validateApplicationName}
                                disabled={isApplicationNameValidateDisabled}
                              >
                                {appNameValidationStatus === 'validating' ? 'Validating...' : appNameValidationStatus === 'valid' ? <><Icon icon="check_circle" size="small" /></> : 'Validate'}
                              </button>
                            </div>
                          )}
                        />
                        {focusedField === 'applicationName' && (
                          <div className="sso-input-tooltip">{tooltipMessages.applicationName}</div>
                        )}
                        {errors.applicationName && (
                          <span className="error-text">{errors.applicationName.message}</span>
                        )}
                        {fieldErrors.applicationName && !errors.applicationName && (
                          <span className="error-text">{fieldErrors.applicationName}</span>
                        )}
                      </div>

                      <div className="sso-form-field">
                        <label className="sso-label">
                          <span className="label-text">{t('Please select the application/services type?')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <div className="sso-radio-group sso-radio-inline">
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="m2mEnvironment"
                              value="public"
                              checked={m2mEnvironment === 'public'}
                              onChange={(e) => {
                                setM2mEnvironment(e.target.value);
                                setHasInteractedM2mEnvironment(true);
                              }}
                            />
                            <span>{t('Public')}</span>
                          </label>
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="m2mEnvironment"
                              value="private"
                              checked={m2mEnvironment === 'private'}
                              onChange={(e) => {
                                setM2mEnvironment(e.target.value);
                                setHasInteractedM2mEnvironment(true);
                              }}
                            />
                            <span>{t('Private')}</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Custom Scope Name - Only when Private */}
                    {m2mEnvironment === 'private' && (
                    <>
                      <div className="sso-form-field m2m-scope-field">
                        <label className="sso-label">
                          <span className="label-text">{t('Custom Scope Name')}</span>
                        </label>
                        <input
                          type="text"
                          className="sso-input"
                          value={newScopeName}
                          onChange={(e) => {
                            setNewScopeName(e.target.value);
                            if (fieldErrors.newScopeName) setFieldErrors((p) => ({ ...p, newScopeName: '' }));
                          }}
                          placeholder={t('Custom Scope Name')}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomScope())}
                        />
                        {fieldErrors.newScopeName && (
                          <span className="error-text">{fieldErrors.newScopeName}</span>
                        )}
                      </div>

                      {/* Add Button */}
                      <div className="sso-add-scope-row">
                        <button 
                          type="button" 
                          className="sso-add-scope-btn"
                          onClick={addCustomScope}
                        >
                          + {t('Add')}
                        </button>
                      </div>

                      {/* Custom Scope Chips */}
                      {customScopes.length > 0 && (
                        <div className="sso-scope-chips">
                          {customScopes.map((scope, index) => (
                            <span key={index} className="sso-scope-chip">
                              {scope}
                              <button 
                                type="button" 
                                className="sso-chip-remove"
                                onClick={() => removeCustomScope(scope)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                    )}
                  </>
                )}

                {/* OIDC Form Fields */}
                {selectedType === 'oidc' && (
                  <>
                    {/* Row 1: Application Name & Application Type */}
                    <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                      <div className="sso-form-field sso-field-with-tooltip">
                        <label className="sso-label">
                          <span className="label-text">{t('Application Name')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="applicationName"
                          control={control}
                          rules={validationRules.applicationName}
                          render={({ field }) => (
                            <div className="sso-input-with-validate">
                              <input
                                {...field}
                                type="text"
                                className="sso-input"
                                placeholder={t('Type...')}
                                maxLength={30}
                                onFocus={() => setFocusedField('applicationName')}
                                onBlur={() => setFocusedField(null)}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setAppNameValidationStatus(null);
                                  setTimeout(checkAppDetailsCompletion, 100);
                                }}
                              />
                              <button
                                type="button"
                                className={`sso-inline-validate-btn ${appNameValidationStatus === 'valid' ? 'valid' : ''}`}
                                onClick={validateApplicationName}
                                disabled={isApplicationNameValidateDisabled}
                              >
                                {appNameValidationStatus === 'validating' ? 'Validating...' : appNameValidationStatus === 'valid' ? <><Icon icon="check_circle" size="small" /></> : 'Validate'}
                              </button>
                            </div>
                          )}
                        />
                        {focusedField === 'applicationName' && (
                          <div className="sso-input-tooltip">{tooltipMessages.applicationName}</div>
                        )}
                        {errors.applicationName && (
                          <span className="error-text">{errors.applicationName.message}</span>
                        )}
                        {fieldErrors.applicationName && !errors.applicationName && (
                          <span className="error-text">{fieldErrors.applicationName}</span>
                        )}
                      </div>

                      <div className="sso-form-field sso-field-with-tooltip">
                        <label className="sso-label">
                          <span className="label-text">{t('Application Type')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="applicationType"
                          control={control}
                          rules={validationRules.applicationType}
                          render={({ field }) => (
                            <div 
                              onFocus={() => setFocusedField('applicationType')}
                              onBlur={() => setFocusedField(null)}
                            >
                              <Dropdown
                                placeholder={t('Select Application Type')}
                                childList={applicationTypeOptions}
                                selectedValue={field.value}
                                setSelectedValue={(val) => {
                                  field.onChange(val);
                                  setTimeout(checkAppDetailsCompletion, 100);
                                }}
                                size="medium"
                                selection="single"
                              />
                            </div>
                          )}
                        />
                        {focusedField === 'applicationType' && (
                          <div className="sso-input-tooltip">{tooltipMessages.applicationType}</div>
                        )}
                        {errors.applicationType && (
                          <span className="error-text">{errors.applicationType.message}</span>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Application URL */}
                    <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                      <div className="sso-form-field sso-field-with-tooltip">
                        <label className="sso-label">
                          <span className="label-text">{t('Application URL')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="applicationUrl"
                          control={control}
                          rules={validationRules.applicationUrl}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              className="sso-input"
                              placeholder="https://example.com"
                              onFocus={() => setFocusedField('applicationUrl')}
                              onBlur={() => setFocusedField(null)}
                              onChange={(e) => {
                                field.onChange(e);
                                setTimeout(checkAppDetailsCompletion, 100);
                              }}
                            />
                          )}
                        />
                        {focusedField === 'applicationUrl' && (
                          <div className="sso-input-tooltip">{tooltipMessages.applicationUrl}</div>
                        )}
                        {errors.applicationUrl && (
                          <span className="error-text">{errors.applicationUrl.message}</span>
                        )}
                      </div>
                    </div>

                  </>
                )}
              </form>
            )}
          </div>

          {/* Access Control Card - Only for SAML */}
          {selectedType === 'saml' && (
            <div className="sso-details-card">
              <div 
                className="sso-details-card-header clickable"
                onClick={() => setIsAccessControlExpanded(!isAccessControlExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Access Control')}</h2>
                <Icon 
                  icon={isAccessControlExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                  size="medium" 
                />
              </div>

              {isAccessControlExpanded && (
                <div className="sso-section-content sso-access-control-content">
                  {/* Row 1: Do you need IDX Attribute + Application Access Control */}
                  <div className={`sso-form-row sso-access-control-top-row ${isMobile ? 'mobile' : ''}`}>
                    {/* Do you need to define access control for IDX Attribute? */}
                    <div className="sso-form-field">
                      <label className="sso-label">
                        <span className="label-text">{t('Do you need to define access control for IDX Attribute?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="needIdxAttribute"
                            value="yes"
                            checked={needIdxAttribute === 'yes'}
                            onChange={(e) => setNeedIdxAttribute(e.target.value)}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label" title={isRestrictedOrConfidential ? t('Not allowed for Restricted/Confidential data') : ''}>
                          <input
                            type="radio"
                            name="needIdxAttribute"
                            value="no"
                            checked={needIdxAttribute === 'no'}
                            disabled={isRestrictedOrConfidential}
                            onChange={(e) => {
                              setNeedIdxAttribute(e.target.value);
                              setNeedAuthorizationRoles('no');
                            }}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                      {isRestrictedOrConfidential && (
                        <span className="error-text" style={{ color: '#666', fontStyle: 'italic' }}>
                          {t('"No" is not allowed for Restricted/Confidential data classification')}
                        </span>
                      )}
                    </div>

                    {/* Application Access Control Dropdown - Show only when IDX access control is not needed */}
                    {needIdxAttribute === 'no' && (
                      <div className="sso-form-field">
                        <Controller
                          name="applicationAccessControl"
                          control={control}
                          rules={{
                            validate: (val) =>
                              needIdxAttribute !== 'no' || (Array.isArray(val) && val.length > 0) || t('Application Access Control is required')
                          }}
                          render={({ field, fieldState }) => (
                            <Dropdown
                              label={t('Application Access Control')}
                              required
                              isRequired
                              placeholder={t('Select')}
                              childList={applicationAccessControlOptions}
                              showLabelIcon={false}
                              selectedValue={field.value}
                              setSelectedValue={(val) => {
                                field.onChange(val);
                              }}
                              size="medium"
                              selection="multiple"
                              isInvalid={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  {/* Do you have existing IDX Attribute? - Only show if needIdxAttribute is yes */}
                  {needIdxAttribute === 'yes' && (
                    <>
                      <div className="sso-form-field" style={{ marginTop: '16px' }}>
                        <label className="sso-label">
                          <span className="label-text">{t('Do you have existing IDX Attribute?')}</span>
                        </label>
                        <div className="sso-radio-group">
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="hasExistingIdxAttribute"
                              value="yes"
                              checked={hasExistingIdxAttribute === 'yes'}
                              onChange={(e) => {
                                setHasExistingIdxAttribute(e.target.value);
                                setIdxAttributeValue('');
                                setIdxValidationStatus(null);
                                setFieldErrors(p => ({ ...p, idxAttributeValue: '' }));
                              }}
                            />
                            <span>{t('Yes')}</span>
                          </label>
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="hasExistingIdxAttribute"
                              value="no"
                              checked={hasExistingIdxAttribute === 'no'}
                              onChange={(e) => {
                                setHasExistingIdxAttribute(e.target.value);
                                setIdxAttributeValue('');
                                setIdxValidationStatus(null);
                                setFieldErrors(p => ({ ...p, idxAttributeValue: '' }));
                              }}
                            />
                            <span>{t('No')}</span>
                          </label>
                        </div>
                      </div>

                      {/* IDX Attribute Input - Always with prefix/suffix for SAML */}
                      <div className={`sso-form-row sso-access-highlight-box ${isMobile ? 'mobile' : ''}`} style={{ marginTop: '16px' }}>
                        <div 
                          className="sso-form-field sso-field-with-idx-tooltip"
                          onMouseEnter={() => setFocusedField('samlIdxAttribute')}
                          onMouseLeave={() => focusedField === 'samlIdxAttribute' && setFocusedField(null)}
                        >
                          <label className="sso-label">
                            <span className="label-text">
                              {hasExistingIdxAttribute === 'yes' 
                                ? t('Provide your existing IDX Attribute')
                                : t('Create new IDX Attribute')}
                            </span>
                            <span className="required">*{t('Required')}</span>
                          </label>
                          <div className="sso-idx-input-container">
                            {hasExistingIdxAttribute === 'no' && <div className="sso-idx-prefix">{idxAttributePrefix}</div>}
                            <input
                              type="text"
                              className={`sso-input ${hasExistingIdxAttribute === 'no' ? 'sso-idx-input' : ''}`}
                              value={idxAttributeValue}
                              maxLength={hasExistingIdxAttribute === 'yes' ? 30 : 8}
                              onChange={(e) => handleIdxAttributeValueChange(e.target.value)}
                              onFocus={() => setFocusedField('samlIdxAttribute')}
                              onBlur={() => setFocusedField(null)}
                              placeholder={hasExistingIdxAttribute === 'yes' ? 'pepappleanixstatus' : 'leanix123'}
                            />
                            {hasExistingIdxAttribute === 'no' && <div className="sso-idx-suffix">{t('status')}</div>}
                          </div>
                          {fieldErrors.idxAttributeValue && (
                            <span className="error-text">{fieldErrors.idxAttributeValue}</span>
                          )}
                          {focusedField === 'samlIdxAttribute' && (
                            <div className="sso-idx-help-tooltip">
                              {tooltipMessages.idxAttributeHelp}
                            </div>
                          )}
                        </div>

                        <div className="sso-form-field">
                          <label className="sso-label">
                            <span className="label-text">{t('Preview:')}</span>
                          </label>
                          <div className="sso-idx-preview-container" style={{marginTop: '15px'}}>
                            <span className="sso-idx-preview">{getIdxPreview()}</span>
                            {idxValidationStatus === 'valid' ? (
                              <span className="sso-validation-status valid">
                                <Icon icon="check_circle" size="small" />
                                {t('Validated')}
                              </span>
                            ) : (
                              <div 
                                onMouseEnter={() => setFocusedField('validateButton')}
                                onMouseLeave={() => setFocusedField(null)}
                              >
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={validateIdxAttribute}
                                  disabled={!idxAttributeValue || idxValidationStatus === 'validating'}
                                >
                                  {idxValidationStatus === 'validating' ? t('Validating...') : t('Validate')}
                                </Button>
                                {focusedField === 'validateButton' && (
                                  <div className="sso-button-tooltip">
                                    {tooltipMessages.validateButton}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Do you need authorization Roles? */}
                  {needIdxAttribute === 'yes' && (
                    <div className="sso-form-field sso-access-control-subsection" style={{ marginTop: '16px' }}>
                      <label className="sso-label">
                        <span className="label-text">{t('Do you need authorization Roles?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="needAuthorizationRolesAccess"
                            value="yes"
                            checked={needAuthorizationRoles === 'yes'}
                            onChange={(e) => {
                              setNeedAuthorizationRoles(e.target.value);
                              setHasInteractedAuthorizationRolesChoice(true);
                            }}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="needAuthorizationRolesAccess"
                            value="no"
                            checked={needAuthorizationRoles === 'no'}
                            onChange={(e) => {
                              setNeedAuthorizationRoles(e.target.value);
                              setHasInteractedAuthorizationRolesChoice(true);
                            }}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Authorization Roles Section - Only if needAuthorizationRoles is yes */}
                  {needAuthorizationRoles === 'yes' && (
                    <>
                      {/* Do you have existing IDX Attribute for role? */}
                      <div className="sso-form-field" style={{ marginTop: '16px' }}>
                        <label className="sso-label">
                          <span className="label-text">{t('Do you have existing IDX Attribute for role?')}</span>
                        </label>
                        {hasExistingIdxForRole === 'no' && (
                          <span className="error-text" style={{ color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
                            {t('Use the same IDX base attribute for both status and role. Role IDX is auto-derived from the status IDX value.')}
                          </span>
                        )}
                        <div className="sso-radio-group">
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="hasExistingIdxForRole"
                              value="yes"
                              checked={hasExistingIdxForRole === 'yes'}
                              disabled={shouldDisableRoleIdxExistingYesOption}
                              onChange={(e) => {
                                setHasExistingIdxForRole(e.target.value);
                                setRoleIdxAttributeValue('');
                                setRoleIdxValidationStatus(null);
                                setAuthorizationRoles([]);
                                setNewRoleName('');
                                setFieldErrors((p) => ({ ...p, newRoleName: '' }));
                              }}
                            />
                            <span>{t('Yes')}</span>
                          </label>
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="hasExistingIdxForRole"
                              value="no"
                              checked={hasExistingIdxForRole === 'no'}
                              onChange={(e) => {
                                setHasExistingIdxForRole(e.target.value);
                                setRoleIdxAttributeValue('');
                                setRoleIdxValidationStatus(null);
                                setAuthorizationRoles([]);
                                setNewRoleName('');
                                setFieldErrors((p) => ({ ...p, newRoleName: '' }));
                              }}
                            />
                            <span>{t('No')}</span>
                          </label>
                        </div>
                      </div>

                      {/* Role IDX Attribute Input - Always with prefix/suffix for SAML */}
                      <div className={`sso-form-row sso-access-highlight-box ${isMobile ? 'mobile' : ''}`} style={{ marginTop: '16px' }}>
                        <div 
                          className="sso-form-field sso-field-with-idx-tooltip"
                          onMouseEnter={() => setFocusedField('samlRoleIdxAttribute')}
                          onMouseLeave={() => focusedField === 'samlRoleIdxAttribute' && setFocusedField(null)}
                        >
                          <label className="sso-label">
                            <span className="label-text">
                              {hasExistingIdxForRole === 'yes' 
                                ? t('Provide your existing IDX Attribute')
                                : t('Derived IDX Attribute')}
                            </span>
                            {hasExistingIdxForRole === 'yes' && (
                              <span className="required">*{t('Required')}</span>
                            )}
                          </label>
                          {hasExistingIdxForRole === 'yes' ? (
                            <div className="sso-idx-input-container">
                              <input
                                type="text"
                                className="sso-input"
                                value={getDerivedRoleIdxInputValue()}
                                maxLength={30}
                                onChange={(e) => handleRoleIdxAttributeValueChange(e.target.value)}
                                onFocus={() => setFocusedField('samlRoleIdxAttribute')}
                                onBlur={() => setFocusedField(null)}
                                placeholder="pepappleanixroles"
                              />
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                              <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#1a1a1a' }}>{getRoleIdxNoCaseText() || '-'}</p>
                              {roleIdxValidationStatus === 'valid' ? (
                                <span className="sso-validation-status valid" style={{ display: 'inline-flex', marginLeft: 'auto' }}>
                                  <Icon icon="check_circle" size="small" />
                                  {t('Validated')}
                                </span>
                              ) : (
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => validateRoleIdxAttribute({ showPopupOnNew: true })}
                                  disabled={!getDerivedRoleIdxInputValue().trim() || roleIdxValidationStatus === 'validating'}
                                >
                                  {roleIdxValidationStatus === 'validating' ? t('Validating...') : t('Validate')}
                                </Button>
                              )}
                            </div>
                          )}
                          {fieldErrors.roleIdxAttributeValue && (
                            <span className="error-text">{fieldErrors.roleIdxAttributeValue}</span>
                          )}
                          {focusedField === 'samlRoleIdxAttribute' && (
                            <div className="sso-idx-help-tooltip">
                              {hasExistingIdxForRole === 'no'
                                ? tooltipMessages.derivedRoleIdxHelp
                                : tooltipMessages.idxAttributeHelp}
                            </div>
                          )}
                        </div>

                        {hasExistingIdxForRole === 'yes' && (
                          <div className="sso-form-field">
                            <label className="sso-label">
                              <span className="label-text">{t('Preview:')}</span>
                            </label>
                            <div className="sso-idx-preview-container" style={{marginTop: '15px'}}>
                              <span className="sso-idx-preview">{getRoleIdxPreview()}</span>
                              {roleIdxValidationStatus === 'valid' ? (
                                <span className="sso-validation-status valid">
                                  <Icon icon="check_circle" size="small" />
                                  {t('Validated')}
                                </span>
                              ) : (
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => validateRoleIdxAttribute()}
                                  disabled={!getDerivedRoleIdxInputValue().trim() || roleIdxValidationStatus === 'validating'}
                                >
                                  {roleIdxValidationStatus === 'validating' ? t('Validating...') : t('Validate')}
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Authorization Roles Input with Add button and SAML Preview */}
                      <div className={`sso-form-row sso-access-control-roles-row ${isMobile ? 'mobile' : ''}`} style={{ marginTop: '16px' }}>
                        <div className="sso-form-field">
                          <label className="sso-label">
                            <span className="label-text">{t('Authorization Roles')}</span>
                          </label>
                          <div className="sso-roles-input-container" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              className="sso-input"
                              value={newRoleName}
                              onChange={(e) => {
                                const val = e.target.value;
                                setNewRoleName(val);
                                if (val && !validators.rolePattern.test(val)) {
                                  setFieldErrors(prev => ({ ...prev, newRoleName: 'Role must be 4-64 alphanumeric characters, can include _ - ( )' }));
                                } else {
                                  setFieldErrors(prev => ({ ...prev, newRoleName: '' }));
                                }
                              }}
                              placeholder={t('Authorization Roles')}
                              maxLength={64}
                              style={{ flex: 1 }}
                            />
                            <Button
                              className="sso-access-add-btn"
                              variant="primary"
                              size="small"
                              onClick={addAuthorizationRole}
                              disabled={!newRoleName.trim() || authorizationRoles.length >= 10}
                            >
                              + {t('Add')}
                            </Button>
                          </div>
                          {fieldErrors.newRoleName && (
                            <span className="error-text">{fieldErrors.newRoleName}</span>
                          )}
                          {/* Display added roles as chips */}
                          {authorizationRoles.length > 0 && (
                            <div className="sso-roles-list" style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {authorizationRoles.map((role, index) => (
                                <div key={index} className="sso-chip">
                                  <span>{role.name}</span>
                                  <button 
                                    type="button" 
                                    className="sso-chip-remove"
                                    onClick={() => removeAuthorizationRole(role.name)}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* <div className="sso-form-field">
                          <label className="sso-label">
                            <span className="label-text">{t('SAML Preview:')}</span>
                          </label>
                          <div className="sso-saml-preview">
                            {authorizationRoles.length > 0 ? (
                              <button type="button" className="sso-saml-preview-trigger">{t('Show')}</button>
                            ) : <span className="sso-saml-preview-placeholder">--</span>}
                          </div>
                        </div> */}
                      </div>

                    </>
                  )}

                </div>
              )}
            </div>
          )}

          {/* Elevated Permissions Card - Only for SAML */}
          {selectedType === 'saml' && (
            <div className="sso-details-card">
              <div 
                className="sso-details-card-header clickable"
                onClick={() => setIsElevatedPermissionsExpanded(!isElevatedPermissionsExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Elevated Permissions')}</h2>
                <Icon 
                  icon={isElevatedPermissionsExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                  size="medium" 
                />
              </div>

              {isElevatedPermissionsExpanded && (
                <div className="sso-section-content">
                  <div className="sso-form-field">
                    <label className="sso-label">
                      <span className="label-text">{t('Is application designed for administrators with elevated permissions, with restricted access for standard users ?')}</span>
                    </label>
                    <div className="sso-radio-group">
                      <label className="sso-radio-label">
                        <input
                          type="radio"
                          name="elevatedPermissions"
                          value="yes"
                          checked={isAdminApp === 'yes'}
                          onChange={(e) => {
                            setIsAdminApp(e.target.value);
                            setHasInteractedElevatedPermissions(true);
                            setIsAppLogoExpanded(true);
                          }}
                        />
                        <span>{t('Yes')}</span>
                      </label>
                      <label className="sso-radio-label">
                        <input
                          type="radio"
                          name="elevatedPermissions"
                          value="no"
                          checked={isAdminApp === 'no'}
                          onChange={(e) => {
                            setIsAdminApp(e.target.value);
                            setHasInteractedElevatedPermissions(true);
                            setIsAppLogoExpanded(true);
                          }}
                        />
                        <span>{t('No')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Application Logo Card - Only for SAML */}
          {selectedType === 'saml' && (
            <div className="sso-details-card">
              <div 
                className="sso-details-card-header clickable"
                onClick={() => setIsAppLogoExpanded(!isAppLogoExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Application Logo')}</h2>
                <Icon 
                  icon={isAppLogoExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                  size="medium" 
                />
              </div>

              {isAppLogoExpanded && (
                <div className="sso-section-content">
                  <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                    {/* Do you have your logo? */}
                    <div className="sso-form-field">
                      <label className="sso-label">
                        <span className="label-text">{t('Do you have your logo?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="samlHasOwnLogo"
                            value="yes"
                            checked={hasOwnLogo === 'yes'}
                            onChange={(e) => {
                              setHasOwnLogo(e.target.value);
                              setHasInteractedLogoChoice(true);
                            }}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="samlHasOwnLogo"
                            value="no"
                            checked={hasOwnLogo === 'no'}
                            onChange={(e) => {
                              setHasOwnLogo(e.target.value);
                              setHasInteractedLogoChoice(true);
                            }}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Application Logo Upload */}
                  {hasOwnLogo === 'yes' && (
                    <div className="sso-form-field" style={{ marginTop: '16px' }}>
                      <div className="sso-upload-section">
                        <div className="sso-logo-preview-container">
                          <label className="sso-label">
                            <span className="label-text">{t('Application Logo')}</span>
                            <span className="required">*{t('Required')}</span>
                          </label>
                          <div
                            className="sso-logo-preview-area"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleLogoDrop}
                          >
                            {applicationLogo ? (
                              <div className="sso-upload-preview-content">
                                <button
                                  type="button"
                                  className="sso-upload-remove-btn"
                                  onClick={removeLogoFile}
                                  aria-label={t('Remove uploaded file')}
                                >
                                  <Icon icon="close" size="small" />
                                </button>
                                <img 
                                  src={URL.createObjectURL(applicationLogo)} 
                                  alt="Logo preview" 
                                  className="sso-logo-preview-image"
                                />
                                <p className="sso-file-name sso-file-name--overlay">
                                  {applicationLogo.name}
                                </p>
                              </div>
                            ) : (
                              <>
                                <Icon icon="cloud_upload" size="large" className="sso-upload-icon" />
                                <p className="sso-upload-text">{t('Drag and drop your file here')}</p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={samlFileInputRef}
                            onChange={handleFileUpload}
                            accept=".png,.jpg,.jpeg,.svg,.gif"
                            style={{ display: 'none' }}
                          />
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => samlFileInputRef.current?.click()}
                            className="sso-upload-btn"
                          >
                            <Icon icon="cloud_upload" size="small" />
                            {t('Upload file')}
                          </Button>
                        </div>
                        <div className="sso-upload-guidelines">
                          <div className="sso-guidelines-header">
                            <Icon icon="description" size="small" />
                            <h4>{t('Upload File Guidelines')}</h4>
                          </div>
                          <ul>
                            <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Allowed File Types: Must be PNG, JPG, SVG, or GIF')}</li>
                            <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Maximum file size: Less than 1MB')}</li>
                            <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Minimum 420px by 120px to prevent upscaling')}</li>
                            <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Transparent background')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Certification Details Card - Only for Restricted/Confidential data classification */}
          {selectedType === 'saml' && isRestrictedOrConfidential && (
            <div className="sso-details-card">
              <div 
                className="sso-details-card-header clickable"
                onClick={() => setIsCertificationDetailsExpanded(!isCertificationDetailsExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Certification Details')}</h2>
                <div className="sso-details-card-actions">
                  {/* <span className="sso-required-indicator"></span> */}
                  <Icon 
                    icon={isCertificationDetailsExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                    size="medium" 
                  />
                </div>
              </div>

              {isCertificationDetailsExpanded && (
                <div className="sso-section-content">
                  <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                    {/* Do you have Signing Certificate Available? */}
                    <div className="sso-form-field">
                      <label className="sso-label">
                        <span className="label-text">{t('Do you have Signing Certificate Available?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="hasSigningCert"
                            value="yes"
                            checked={hasSigningCert === 'yes'}
                            onChange={(e) => {
                              setHasSigningCert(e.target.value);
                              setHasInteractedSigningCertChoice(true);
                              setFieldErrors((p) => ({ ...p, signingCert: '', businessJustification: '' }));
                              clearErrors('businessJustification');
                            }}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="hasSigningCert"
                            value="no"
                            checked={hasSigningCert === 'no'}
                            onChange={(e) => {
                              setHasSigningCert(e.target.value);
                              setHasInteractedSigningCertChoice(true);
                              removeSigningCertFile();
                              setFieldErrors((p) => ({ ...p, businessJustification: '' }));
                            }}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                    </div>

                    {/* Business Justification - Only when No is selected */}
                    {hasSigningCert === 'no' && (
                      <div 
                        className="sso-form-field sso-field-with-tooltip"
                        onMouseEnter={() => setFocusedField('businessJustification')}
                        onMouseLeave={() => focusedField === 'businessJustification' && setFocusedField(null)}
                      >
                        <label className="sso-label">
                          <span className="label-text">{t('Business Justification')}</span>
                          <span className="required">*{t('Required')}</span>
                        </label>
                        <Controller
                          name="businessJustification"
                          control={control}
                          rules={validationRules.businessJustification}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              className="sso-input"
                              value={businessJustification}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                setBusinessJustification(e.target.value);
                                setFieldErrors((p) => ({ ...p, businessJustification: '' }));
                              }}
                              onFocus={() => setFocusedField('businessJustification')}
                              onBlur={(e) => {
                                field.onBlur(e);
                                setFocusedField(null);
                              }}
                              placeholder={t('Type...')}
                              maxLength={500}
                            />
                          )}
                        />
                        {errors.businessJustification && (
                          <span className="error-text">{errors.businessJustification.message}</span>
                        )}
                        {fieldErrors.businessJustification && !errors.businessJustification && (
                          <span className="error-text">{fieldErrors.businessJustification}</span>
                        )}
                        {focusedField === 'businessJustification' && (
                          <div className="sso-input-tooltip">
                            {t('Please provide business justification if signing certificate is not available.')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Signing Certificate Upload - Only when Yes is selected */}
                  {hasSigningCert === 'yes' && (
                    <div className="sso-form-field" style={{ marginTop: '16px' }}>
                      <div className="sso-form-field" style={{ marginBottom: '12px' }}>
                        {/* <label className="sso-label">
                          <span className="label-text">{t('Certificate Input Method')}</span>
                        </label> */}
                        <div className="sso-radio-group">
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="signingCertEntryMode"
                              value="file"
                              checked={signingCertEntryMode === 'file'}
                              onChange={(e) => {
                                setSigningCertEntryMode(e.target.value);
                                setSigningCertText('');
                                setSigningCertDetails(null);
                                setSigningCertDaysRemaining(null);
                                setSigningCertTextValidated(false);
                                setFieldErrors((p) => ({ ...p, signingCert: '' }));
                              }}
                            />
                            <span>{t('Upload File')}</span>
                          </label>
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="signingCertEntryMode"
                              value="text"
                              checked={signingCertEntryMode === 'text'}
                              onChange={(e) => {
                                setSigningCertEntryMode(e.target.value);
                                removeSigningCertFile();
                                setFieldErrors((p) => ({ ...p, signingCert: '' }));
                              }}
                            />
                            <span>{t('Certificate Text')}</span>
                          </label>
                        </div>
                      </div>

                      {signingCertEntryMode === 'file' ? (
                        <div className="sso-upload-section">
                          <div className="sso-logo-preview-container">
                            <label className="sso-label">
                              <span className="label-text">{t('Signing Certificate')}</span>
                              <span className="required">*{t('Required')}</span>
                            </label>
                            <div 
                              className="sso-logo-preview-area"
                              onClick={() => signingCertInputRef.current?.click()}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={handleSigningCertDrop}
                            >
                              {signingCertFile ? (
                                <div className="sso-upload-preview-content">
                                  <button
                                    type="button"
                                    className="sso-upload-remove-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeSigningCertFile();
                                    }}
                                    aria-label={t('Remove uploaded file')}
                                  >
                                    <Icon icon="close" size="small" />
                                  </button>
                                  <Icon icon="check_circle" size="large" style={{ color: '#10b981' }} />
                                  <p className="sso-file-name sso-file-name--overlay">{signingCertFile.name}</p>
                                </div>
                              ) : (
                                <>
                                  <Icon icon="cloud_upload" size="large" className="sso-upload-icon" />
                                  <p className="sso-upload-text">{t('Drag and drop your file here')}</p>
                                </>
                              )}
                            </div>
                            <input
                              type="file"
                              ref={signingCertInputRef}
                              onChange={handleSigningCertUpload}
                              accept=".cer,.pem,.crt"
                              style={{ display: 'none' }}
                            />
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => signingCertInputRef.current?.click()}
                              className="sso-upload-btn"
                            >
                              <Icon icon="cloud_upload" size="small" />
                              {t('Upload file')}
                            </Button>
                            {fieldErrors.signingCert && (
                              <span className="error-text">{fieldErrors.signingCert}</span>
                            )}
                          </div>
                          <div className="sso-upload-guidelines">
                            <div className="sso-guidelines-header">
                              <Icon icon="description" size="small" />
                              <h4>{t('Upload File Guidelines')}</h4>
                            </div>
                            <ul>
                              <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Allowed File Types: Must be .cer, .pem, .crt')}</li>
                              <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Maximum file size: Less than 1MB')}</li>
                              <li><Icon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Minimum validity of 30 days from the current date')}</li>
                              {/* <li><Icon icon="check_circle_outline" size="small" className="sso-guideline-icon" /> {t('Minimum 420px by 120px to prevent upscaling')}</li> */}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <TextArea
                            label={t('Signing Certificate (PEM Text)')}
                            placeholder={t('Paste certificate content')}
                            rows={8}
                            required={true}
                            showToolbar={false}
                            showCharacterCount={false}
                            value={signingCertText}
                            onChange={(e) => handleSigningCertTextChange(e.target.value)}
                            style={{ width: '100%' }}
                          />
                          <div style={{ marginTop: '8px' }}>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={handleDecodeSigningCertText}
                            >
                                {signingCertTextValidated ? t('Validated') : t('Validate Certificate')}
                            </Button>
                          </div>
                          {fieldErrors.signingCert && (
                            <span className="error-text">{fieldErrors.signingCert}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* OIDC Configurations Card - Only for OIDC (not M2M) */}
          {selectedType === 'oidc' && (
            <div className="sso-details-card">
              <div 
                className="sso-details-card-header clickable"
                onClick={() => setIsTechnicalConfigExpanded(!isTechnicalConfigExpanded)}
              >
                <h2 className="sso-details-card-title">{t('OIDC Configurations')}</h2>
                <Icon 
                  icon={isTechnicalConfigExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                  size="medium" 
                />
              </div>

              {isTechnicalConfigExpanded && (
                <div className="sso-technical-config-content">
                  {/* Login URLs */}
                  <div className="sso-form-field sso-field-with-indicator">
                    <label className="sso-label">
                      <span className="label-text">{t('Login URL')}</span>
                      <span className="sso-required-dot"></span>
                      <span className="required" style={{ marginLeft: 'auto' }}>*{t('Required')}</span>
                    </label>
                    {loginUrls.map((url, index) => (
                      <div key={index}>
                        <div className="sso-url-input-row">
                          <input
                            type="text"
                            className="sso-input"
                            value={url}
                            onChange={(e) => handleLoginUrlChange(index, e.target.value)}
                            placeholder="https://example.com"
                          />
                          {loginUrls.length > 1 && index > 0 && (
                            <ButtonIcon
                              icon="delete"
                              size="small"
                              variant="naked"
                              onClick={() => removeLoginUrl(index)}
                            />
                          )}
                        </div>
                        {fieldErrors.loginUrls && fieldErrors.loginUrls[index] && (
                          <span className="error-text">{fieldErrors.loginUrls[index]}</span>
                        )}
                      </div>
                    ))}
                    {loginUrls.length < 3 && (
                      <button type="button" className="sso-add-btn" onClick={addLoginUrl}>
                        + {t('Add')}
                      </button>
                    )}
                  </div>

                  {/* Logout URL */}
                  <div className="sso-form-field" style={{ marginTop: '16px' }}>
                    <label className="sso-label">
                      <span className="label-text">{t('Logout URL')}</span>
                      <span className="required" style={{ marginLeft: 'auto' }}>*{t('Required')}</span>
                    </label>
                    {logoutUrls.map((url, index) => (
                      <div key={index}>
                        <div className="sso-url-input-row">
                          <input
                            type="text"
                            className="sso-input"
                            value={url}
                            onChange={(e) => handleLogoutUrlChange(index, e.target.value)}
                            placeholder="https://example.com"
                          />
                          {logoutUrls.length > 1 && index > 0 && (
                            <ButtonIcon
                              icon="delete"
                              size="small"
                              variant="naked"
                              onClick={() => removeLogoutUrl(index)}
                            />
                          )}
                        </div>
                        {fieldErrors.logoutUrls && fieldErrors.logoutUrls[index] && (
                          <span className="error-text">{fieldErrors.logoutUrls[index]}</span>
                        )}
                      </div>
                    ))}
                    {fieldErrors.logoutUrl && (
                      <span className="error-text">{fieldErrors.logoutUrl}</span>
                    )}
                    {logoutUrls.length < 3 && (
                      <button type="button" className="sso-add-btn" onClick={addLogoutUrl}>
                        + {t('Add')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Access Control Card - Only for OIDC */}
          {selectedType === 'oidc' && (
            <div className="sso-details-card">
              <div 
                className="sso-details-card-header clickable"
                onClick={() => setIsAccessControlExpanded(!isAccessControlExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Access Control')}</h2>
                <Icon 
                  icon={isAccessControlExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} 
                  size="medium" 
                />
              </div>

              {isAccessControlExpanded && (
                <div className="sso-section-content sso-access-control-content">
                  {/* Row 1: Do you need IDX Attribute + Application Access Control */}
                  <div className={`sso-form-row sso-access-control-top-row ${isMobile ? 'mobile' : ''}`}>
                    {/* Do you need to define access control for IDX Attribute? */}
                    <div className="sso-form-field">
                      <label className="sso-label">
                        <span className="label-text">{t('Do you need to define access control for IDX Attribute?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="oidcNeedIdxAttribute"
                            value="yes"
                            checked={needIdxAttribute === 'yes'}
                            onChange={(e) => setNeedIdxAttribute(e.target.value)}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label" title={isRestrictedOrConfidential ? t('Not allowed for Restricted/Confidential data') : ''}>
                          <input
                            type="radio"
                            name="oidcNeedIdxAttribute"
                            value="no"
                            checked={needIdxAttribute === 'no'}
                            disabled={isRestrictedOrConfidential}
                            onChange={(e) => {
                              setNeedIdxAttribute(e.target.value);
                              setNeedAuthorizationRoles('no');
                            }}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                      {isRestrictedOrConfidential && (
                        <span className="error-text" style={{ color: '#666', fontStyle: 'italic' }}>
                          {t('"No" is not allowed for Restricted/Confidential data classification')}
                        </span>
                      )}
                    </div>

                    {/* Application Access Control Dropdown - Show only when IDX access control is not needed */}
                    {needIdxAttribute === 'no' && (
                      <div className="sso-form-field">
                        <Controller
                          name="applicationAccessControl"
                          control={control}
                          rules={{
                            validate: (val) =>
                              needIdxAttribute !== 'no' || (Array.isArray(val) && val.length > 0) || t('Application Access Control is required')
                          }}
                          render={({ field, fieldState }) => (
                            <Dropdown
                              label={t('Application Access Control')}
                              required
                              isRequired
                              placeholder={t('Select')}
                              childList={applicationAccessControlOptions}
                              showLabelIcon={false}
                              selectedValue={field.value}
                              setSelectedValue={(val) => {
                                field.onChange(val);
                              }}
                              size="medium"
                              selection="multiple"
                              isInvalid={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  {/* Do you have existing IDX Attribute? - Only show if needIdxAttribute is yes */}
                  {needIdxAttribute === 'yes' && (
                    <>
                      <div className="sso-form-field" style={{ marginTop: '16px' }}>
                        <label className="sso-label">
                          <span className="label-text">{t('Do you have existing IDX Attribute?')}</span>
                        </label>
                        <div className="sso-radio-group">
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="oidcHasExistingIdxAttribute"
                              value="yes"
                              checked={hasExistingIdxAttribute === 'yes'}
                              onChange={(e) => {
                                setHasExistingIdxAttribute(e.target.value);
                                setIdxAttributeValue('');
                                setIdxValidationStatus(null);
                                setFieldErrors(p => ({ ...p, idxAttributeValue: '' }));
                              }}
                            />
                            <span>{t('Yes')}</span>
                          </label>
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="oidcHasExistingIdxAttribute"
                              value="no"
                              checked={hasExistingIdxAttribute === 'no'}
                              onChange={(e) => {
                                setHasExistingIdxAttribute(e.target.value);
                                setIdxAttributeValue('');
                                setIdxValidationStatus(null);
                                setFieldErrors(p => ({ ...p, idxAttributeValue: '' }));
                              }}
                            />
                            <span>{t('No')}</span>
                          </label>
                        </div>
                      </div>

                      {/* IDX Attribute Input */}
                      <div className={`sso-form-row sso-access-highlight-box ${isMobile ? 'mobile' : ''}`} style={{ marginTop: '16px' }}>
                        <div 
                          className="sso-form-field sso-field-with-idx-tooltip"
                          onMouseEnter={() => setFocusedField('oidcIdxAttribute')}
                          onMouseLeave={() => focusedField === 'oidcIdxAttribute' && setFocusedField(null)}
                        >
                          <label className="sso-label">
                            <span className="label-text">
                              {hasExistingIdxAttribute === 'yes' 
                                ? t('Provide your existing IDX Attribute')
                                : t('Create new IDX Attribute')}
                            </span>
                            <span className="required">*{t('Required')}</span>
                          </label>
                          <div className="sso-idx-input-container">
                            {hasExistingIdxAttribute === 'no' && <div className="sso-idx-prefix">{idxAttributePrefix}</div>}
                            <input
                              type="text"
                              className={`sso-input ${hasExistingIdxAttribute === 'no' ? 'sso-idx-input' : ''}`}
                              value={idxAttributeValue}
                              maxLength={hasExistingIdxAttribute === 'yes' ? 30 : 8}
                              onChange={(e) => handleIdxAttributeValueChange(e.target.value)}
                              onFocus={() => setFocusedField('oidcIdxAttribute')}
                              onBlur={() => setFocusedField(null)}
                              placeholder={hasExistingIdxAttribute === 'yes' ? 'pepappleanixstatus' : 'leanix123'}
                            />
                            {hasExistingIdxAttribute === 'no' && <div className="sso-idx-suffix">{t('status')}</div>}
                          </div>
                          {fieldErrors.idxAttributeValue && (
                            <span className="error-text">{fieldErrors.idxAttributeValue}</span>
                          )}
                          {focusedField === 'oidcIdxAttribute' && (
                            <div className="sso-idx-help-tooltip">
                              {tooltipMessages.idxAttributeHelp}
                            </div>
                          )}
                        </div>

                        <div className="sso-form-field">
                          <label className="sso-label">
                            <span className="label-text">{t('Preview:')}</span>
                          </label>
                          <div className="sso-idx-preview-container" style={{marginTop: '15px'}}>
                            <span className="sso-idx-preview">{getIdxPreview()}</span>
                            {idxValidationStatus === 'valid' ? (
                              <span className="sso-validation-status valid">
                                <Icon icon="check_circle" size="small" />
                                {t('Validated')}
                              </span>
                            ) : (
                              <div 
                                onMouseEnter={() => setFocusedField('oidcValidateButton')}
                                onMouseLeave={() => setFocusedField(null)}
                              >
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={validateIdxAttribute}
                                  disabled={!idxAttributeValue || idxValidationStatus === 'validating'}
                                >
                                  {idxValidationStatus === 'validating' ? t('Validating...') : t('Validate')}
                                </Button>
                                {focusedField === 'oidcValidateButton' && (
                                  <div className="sso-button-tooltip">
                                    {tooltipMessages.validateButton}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Do you need authorization Roles? */}
                  {needIdxAttribute === 'yes' && (
                    <div className="sso-form-field sso-access-control-subsection" style={{ marginTop: '16px' }}>
                      <label className="sso-label">
                        <span className="label-text">{t('Do you need authorization Roles?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="oidcNeedAuthorizationRoles"
                            value="yes"
                            checked={needAuthorizationRoles === 'yes'}
                            onChange={(e) => setNeedAuthorizationRoles(e.target.value)}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="oidcNeedAuthorizationRoles"
                            value="no"
                            checked={needAuthorizationRoles === 'no'}
                            onChange={(e) => setNeedAuthorizationRoles(e.target.value)}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Authorization Roles Section - Only if needAuthorizationRoles is yes */}
                  {needAuthorizationRoles === 'yes' && (
                    <>
                      {/* Do you have existing IDX Attribute for role? */}
                      <div className="sso-form-field" style={{ marginTop: '16px' }}>
                        <label className="sso-label">
                          <span className="label-text">{t('Do you have existing IDX Attribute for role?')}</span>
                        </label>
                        {hasExistingIdxForRole === 'no' && (
                          <span className="error-text" style={{ color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
                            {t('Use the same IDX base attribute for both status and role. Role IDX is auto-derived from the status IDX value.')}
                          </span>
                        )}
                        <div className="sso-radio-group">
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="oidcHasExistingIdxForRole"
                              value="yes"
                              checked={hasExistingIdxForRole === 'yes'}
                              disabled={shouldDisableRoleIdxExistingYesOption}
                              onChange={(e) => {
                                setHasExistingIdxForRole(e.target.value);
                                setRoleIdxAttributeValue('');
                                setRoleIdxValidationStatus(null);
                                setAuthorizationRoles([]);
                                setNewRoleName('');
                                setFieldErrors((p) => ({ ...p, newRoleName: '' }));
                              }}
                            />
                            <span>{t('Yes')}</span>
                          </label>
                          <label className="sso-radio-label">
                            <input
                              type="radio"
                              name="oidcHasExistingIdxForRole"
                              value="no"
                              checked={hasExistingIdxForRole === 'no'}
                              onChange={(e) => {
                                setHasExistingIdxForRole(e.target.value);
                                setRoleIdxAttributeValue('');
                                setRoleIdxValidationStatus(null);
                                setAuthorizationRoles([]);
                                setNewRoleName('');
                                setFieldErrors((p) => ({ ...p, newRoleName: '' }));
                              }}
                            />
                            <span>{t('No')}</span>
                          </label>
                        </div>
                      </div>

                      {/* Role IDX Attribute Input - Always with prefix/suffix for OIDC */}
                      <div className={`sso-form-row sso-access-highlight-box ${isMobile ? 'mobile' : ''}`} style={{ marginTop: '16px' }}>
                        <div 
                          className="sso-form-field sso-field-with-idx-tooltip"
                          onMouseEnter={() => setFocusedField('oidcRoleIdxAttribute')}
                          onMouseLeave={() => focusedField === 'oidcRoleIdxAttribute' && setFocusedField(null)}
                        >
                          <label className="sso-label">
                            <span className="label-text">
                              {hasExistingIdxForRole === 'yes' 
                                ? t('Provide your existing IDX Attribute')
                                : t('Derived IDX Attribute')}
                            </span>
                            {hasExistingIdxForRole === 'yes' && (
                              <span className="required">*{t('Required')}</span>
                            )}
                          </label>
                          {hasExistingIdxForRole === 'yes' ? (
                            <div className="sso-idx-input-container">
                              <input
                                type="text"
                                className="sso-input"
                                value={getDerivedRoleIdxInputValue()}
                                maxLength={30}
                                onChange={(e) => handleRoleIdxAttributeValueChange(e.target.value)}
                                onFocus={() => setFocusedField('oidcRoleIdxAttribute')}
                                onBlur={() => setFocusedField(null)}
                                placeholder="pepappleanixroles"
                              />
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                              <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#1a1a1a' }}>{getRoleIdxNoCaseText() || '-'}</p>
                              {roleIdxValidationStatus === 'valid' ? (
                                <span className="sso-validation-status valid" style={{ display: 'inline-flex', marginLeft: 'auto' }}>
                                  <Icon icon="check_circle" size="small" />
                                  {t('Validated')}
                                </span>
                              ) : (
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => validateRoleIdxAttribute({ showPopupOnNew: true })}
                                  disabled={!getDerivedRoleIdxInputValue().trim() || roleIdxValidationStatus === 'validating'}
                                >
                                  {roleIdxValidationStatus === 'validating' ? t('Validating...') : t('Validate')}
                                </Button>
                              )}
                            </div>
                          )}
                          {fieldErrors.roleIdxAttributeValue && (
                            <span className="error-text">{fieldErrors.roleIdxAttributeValue}</span>
                          )}
                          {focusedField === 'oidcRoleIdxAttribute' && (
                            <div className="sso-idx-help-tooltip">
                              {hasExistingIdxForRole === 'no'
                                ? tooltipMessages.derivedRoleIdxHelp
                                : tooltipMessages.idxAttributeHelp}
                            </div>
                          )}
                        </div>

                        {hasExistingIdxForRole === 'yes' && (
                          <div className="sso-form-field">
                            <label className="sso-label">
                              <span className="label-text">{t('Preview:')}</span>
                            </label>
                            <div className="sso-idx-preview-container" style={{marginTop: '15px'}}>
                              <span className="sso-idx-preview">{getRoleIdxPreview()}</span>
                              {roleIdxValidationStatus === 'valid' ? (
                                <span className="sso-validation-status valid">
                                  <Icon icon="check_circle" size="small" />
                                  {t('Validated')}
                                </span>
                              ) : (
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => validateRoleIdxAttribute()}
                                  disabled={!getDerivedRoleIdxInputValue().trim() || roleIdxValidationStatus === 'validating'}
                                >
                                  {roleIdxValidationStatus === 'validating' ? t('Validating...') : t('Validate')}
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Authorization Roles Input with Add button and SAML Preview */}
                      <div className={`sso-form-row sso-access-control-roles-row ${isMobile ? 'mobile' : ''}`} style={{ marginTop: '16px' }}>
                        <div className="sso-form-field">
                          <label className="sso-label">
                            <span className="label-text">{t('Authorization Roles')}</span>
                          </label>
                          <div className="sso-roles-input-container" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              className="sso-input"
                              value={newRoleName}
                              onChange={(e) => {
                                const val = e.target.value;
                                setNewRoleName(val);
                                if (val && !validators.rolePattern.test(val)) {
                                  setFieldErrors(prev => ({ ...prev, newRoleName: 'Role must be 4-64 alphanumeric characters, can include _ - ( )' }));
                                } else {
                                  setFieldErrors(prev => ({ ...prev, newRoleName: '' }));
                                }
                              }}
                              placeholder={t('Authorization Roles')}
                              maxLength={64}
                              style={{ flex: 1 }}
                            />
                            <Button
                              variant="primary"
                              size="small"
                              onClick={addAuthorizationRole}
                              disabled={!newRoleName.trim() || authorizationRoles.length >= 10}
                            >
                              + {t('Add')}
                            </Button>
                          </div>
                          {fieldErrors.newRoleName && (
                            <span className="error-text">{fieldErrors.newRoleName}</span>
                          )}
                          {/* Display added roles as chips */}
                          {authorizationRoles.length > 0 && (
                            <div className="sso-roles-list" style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {authorizationRoles.map((role, index) => (
                                <div key={index} className="sso-chip">
                                  <span>{role.name}</span>
                                  <button 
                                    type="button" 
                                    className="sso-chip-remove"
                                    onClick={() => removeAuthorizationRole(role.name)}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* <div className="sso-form-field">
                          <label className="sso-label">
                            <span className="label-text">{t('SAML Preview:')}</span>
                          </label>
                          <div className="sso-saml-preview">
                            {authorizationRoles.length > 0 ? (
                              authorizationRoles.map((role, index) => (
                                <div key={index} style={{ marginBottom: '4px' }}>{role.name}</div>
                              ))
                            ) : (
                              <span style={{ color: '#999' }}>--</span>
                            )}
                          </div> 
                        </div>*/}
                      </div>

                    </>
                  )}

                </div>
              )}
            </div>
          )}

          {/* Elevated Permissions Card - Only for OIDC (not M2M) */}
          {selectedType === 'oidc' && (
            <div className="sso-details-card">
              <div
                className="sso-details-card-header clickable"
                onClick={() => setIsAdditionalDetailsExpanded(!isAdditionalDetailsExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Elevated Permissions')}</h2>
                <Icon
                  icon={isAdditionalDetailsExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                  size="medium"
                />
              </div>

              {isAdditionalDetailsExpanded && (
                <div className="sso-section-content">
                  <div className="sso-form-field">
                    <label className="sso-label">
                      <span className="label-text">{t('Is application designed for administrators with elevated permissions, with restricted access for standard users?')}</span>
                    </label>
                    <div className="sso-radio-group">
                      <label className="sso-radio-label">
                        <input
                          type="radio"
                          name="isAdminApp"
                          value="yes"
                          checked={isAdminApp === 'yes'}
                          onChange={(e) => {
                            setIsAdminApp(e.target.value);
                            setHasInteractedOidcAdminChoice(true);
                            setIsAppLogoExpanded(true);
                          }}
                        />
                        <span>{t('Yes')}</span>
                      </label>
                      <label className="sso-radio-label">
                        <input
                          type="radio"
                          name="isAdminApp"
                          value="no"
                          checked={isAdminApp === 'no'}
                          onChange={(e) => {
                            setIsAdminApp(e.target.value);
                            setHasInteractedOidcAdminChoice(true);
                            setIsAppLogoExpanded(true);
                          }}
                        />
                        <span>{t('No')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Application Logo Card - Only for OIDC (not M2M) */}
          {selectedType === 'oidc' && (
            <div className="sso-details-card">
              <div
                className="sso-details-card-header clickable"
                onClick={() => setIsAppLogoExpanded(!isAppLogoExpanded)}
              >
                <h2 className="sso-details-card-title">{t('Application Logo')}</h2>
                <Icon
                  icon={isAppLogoExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                  size="medium"
                />
              </div>

              {isAppLogoExpanded && (
                <div className="sso-section-content">
                  <div className={`sso-form-row ${isMobile ? 'mobile' : ''}`}>
                    <div className="sso-form-field">
                      <label className="sso-label">
                        <span className="label-text">{t('Do you have your logo?')}</span>
                      </label>
                      <div className="sso-radio-group">
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="oidcHasOwnLogo"
                            value="yes"
                            checked={hasOwnLogo === 'yes'}
                            onChange={(e) => {
                              setHasOwnLogo(e.target.value);
                              setHasInteractedLogoChoice(true);
                            }}
                          />
                          <span>{t('Yes')}</span>
                        </label>
                        <label className="sso-radio-label">
                          <input
                            type="radio"
                            name="oidcHasOwnLogo"
                            value="no"
                            checked={hasOwnLogo === 'no'}
                            onChange={(e) => {
                              setHasOwnLogo(e.target.value);
                              setHasInteractedLogoChoice(true);
                            }}
                          />
                          <span>{t('No')}</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Application Logo Upload */}
                  {hasOwnLogo === 'yes' && (
                    <div className="sso-form-field" style={{ marginTop: '16px' }}>
                      <div className="sso-upload-section">
                        <div className="sso-logo-preview-container">
                          <label className="sso-label">
                            <span className="label-text">{t('Application Logo')}</span>
                            <span className="required">*{t('Required')}</span>
                          </label>
                          <div
                            className="sso-logo-preview-area"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleLogoDrop}
                          >
                            {applicationLogo ? (
                              <div className="sso-upload-preview-content">
                                <button
                                  type="button"
                                  className="sso-upload-remove-btn"
                                  onClick={removeLogoFile}
                                  aria-label={t('Remove uploaded file')}
                                >
                                  <Icon icon="close" size="small" />
                                </button>
                                <img
                                  src={URL.createObjectURL(applicationLogo)}
                                  alt="Logo preview"
                                  className="sso-logo-preview-image"
                                />
                                <p className="sso-file-name sso-file-name--overlay">
                                  {applicationLogo.name}
                                </p>
                              </div>
                            ) : (
                              <>
                                <Icon icon="cloud_upload" size="large" className="sso-upload-icon" />
                                <p className="sso-upload-text">{t('Drag and drop your file here')}</p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept=".png,.jpg,.jpeg,.svg,.gif"
                            style={{ display: 'none' }}
                          />
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            className="sso-upload-btn"
                          >
                            <Icon icon="cloud_upload" size="small" />
                            {t('Upload file')}
                          </Button>
                        </div>
                        <div className="sso-upload-guidelines">
                          <div className="sso-guidelines-header">
                            <Icon icon="description" size="small" />
                            <h4>{t('Upload File Guidelines')}</h4>
                          </div>
                          <ul>
                            <li><ButtonIcon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Allowed File Types: Must be PNG, JPG, SVG, or GIF')}</li>
                            <li><ButtonIcon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Maximum file size: Less than 1MB')}</li>
                            <li><ButtonIcon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Minimum 420px by 120px to prevent upscaling')}</li>
                            <li><ButtonIcon icon="info" size="small" variant="naked" className="sso-guideline-icon-gray" aria-label={t('Guideline info')} /> {t('Transparent background')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer with Checkbox and Buttons */}
          <div className="sso-details-footer">
            <div className="sso-acknowledge-section">
              <Checkbox
                checked={acknowledgeChecked}
                onUpdate={(checked) => setAcknowledgeChecked(checked)}
                size="small"
              />
              <span className="sso-acknowledge-text">
                {t('I acknowledge that I have reviewed the')}{" "}
                <a href="#" className="sso-link">{t('Help Guide')}</a>{" "}
                {t('for integrating applications with SSO using SAML or OIDC protocols.')}
              </span>
            </div>

            <div className="sso-action-buttons">
              <Button
                variant="secondary"
                size="medium"
                onClick={handleCancel}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={handleSubmit(onSubmit, onFormError)}
                disabled={!acknowledgeChecked}
              >
                {t('Submit')}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Section - Info Cards */}
        <div className="sso-details-sidebar">
          {/* Project Card */}
          <div className="sso-info-card">
            <div className="sso-info-header">
              <Icon icon="description" size="medium" className="sso-info-icon" />
              <h3 className="sso-info-title">{t('Project')}</h3>
            </div>
            <div className="sso-info-links">
              <a href="#" className="sso-link-tag" target="_blank" rel="noopener noreferrer">
                OIDC <Icon icon="open_in_new" size="small" />
              </a>
              <a href="#" className="sso-link-tag" target="_blank" rel="noopener noreferrer">
                OIDC-Machine2Machine(API Service) <Icon icon="open_in_new" size="small" />
              </a>
              <a href="#" className="sso-link-tag" target="_blank" rel="noopener noreferrer">
                SAML <Icon icon="open_in_new" size="small" />
              </a>
            </div>
          </div>

          {/* Help & FAQ Card */}
          <div className="sso-info-card">
            <div className="sso-info-header">
              <Icon icon="help_outline" size="medium" className="sso-info-icon" />
              <h3 className="sso-info-title">{t('Help & FAQ on SSO Integration')}</h3>
            </div>
            <div className="sso-info-links">
              <a href={ASSOCIATE_SSO_FAQ_URL} className="sso-link-button" target="_blank" rel="noopener noreferrer">
                SSO FAQ Site  <Icon icon="open_in_new" size="small" />
              </a>
            </div>
          </div>

          {/* Help Guide Card */}
          <div className="sso-info-card">
            <div className="sso-info-header">
              <Icon icon="menu_book" size="medium" className="sso-info-icon help-guide" />
              <h3 className="sso-info-title"><a href={ASSOCIATE_SSO_FAQ_URL} target="_blank" rel="noopener noreferrer">{t('Help Guide')}</a></h3>
            </div>
            <p className="sso-info-text">
              {t('Please review the')} <a href={ASSOCIATE_SSO_FAQ_URL} className="sso-text-link" target="_blank" rel="noopener noreferrer">{t('Help Guide')}</a> {t('for supported integration patterns, required metadata/configuration parameters, and best practices.')}
            </p>
            <div className="sso-info-note">
              <p>
                {t('For additional support with SSO integrations, submit a Service Request to ASSOCIATE SSO Operations.')}
              </p>
              <p className="sso-note-highlight">
                <strong>{t('Note')}:</strong> {t('SRs are processed per standard SLAs and cannot be expedited.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* New IDX Attribute Validated Popup */}
      {showNewIdxValidatedPopup && (
        <div className="sso-modal-overlay sso-idx-validated-overlay">
          <div className="sso-modal sso-idx-validated-modal">
            <div className="sso-modal-content sso-idx-validated-content">
            <h2 className="sso-modal-title sso-idx-validated-title">
              {t('New IDX Attribute Validated')}
            </h2>
            <p className="sso-modal-subtitle sso-idx-validated-subtitle">
              {t('This attribute is created to allow users to access the application through PepsiCo SSO via either myIDM form or Bulk upload.')}
            </p>
            <div className="sso-modal-actions sso-idx-validated-actions">
              <Button
                variant="primary"
                size="medium"
                onClick={() => {
                  if (newIdxValidatedType === 'idx') {
                    setIdxValidationStatus('valid');
                    if (needAuthorizationRoles === 'yes' && hasExistingIdxForRole === 'no') {
                      setRoleIdxValidationStatus('valid');
                    }
                  } else if (newIdxValidatedType === 'role') {
                    setRoleIdxValidationStatus('valid');
                  }
                  setShowNewIdxValidatedPopup(false);
                  setNewIdxValidatedType(null);
                }}
              >
                {t('Ok, continue')}
              </Button>
            </div>
            </div>
          </div>
        </div>
      )}

      {showSigningCertDetailsPopup && signingCertDetails && (
        <div className="sso-modal-overlay sso-idx-validated-overlay">
          <div className="sso-modal sso-idx-validated-modal" style={{ maxWidth: '760px' }}>
            <div className="sso-modal-content sso-idx-validated-content">
              <h2 className="sso-modal-title sso-idx-validated-title">
                {t('Decoded Certificate Details')}
              </h2>
              <div style={{ display: 'grid', gap: '12px', marginTop: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '10px' }}>
                  <strong>{t('Subject')}</strong>
                  <span style={{ wordBreak: 'break-word' }}>{signingCertDetails.subject}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '10px' }}>
                  <strong>{t('Issuer')}</strong>
                  <span style={{ wordBreak: 'break-word' }}>{signingCertDetails.issuer}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '10px' }}>
                  <strong>{t('Serial Number')}</strong>
                  <span style={{ wordBreak: 'break-word' }}>{signingCertDetails.serialNumber}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '10px' }}>
                  <strong>{t('Valid From')}</strong>
                  <span>{signingCertDetails.validFrom}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '10px' }}>
                  <strong>{t('Valid To')}</strong>
                  <span>{signingCertDetails.validTo}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '10px' }}>
                  <strong>{t('Days Remaining')}</strong>
                  <span>{signingCertDetails.daysRemaining}</span>
                </div>
              </div>
              <div className="sso-modal-actions sso-idx-validated-actions" style={{ marginTop: '16px' }}>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => setShowSigningCertDetailsPopup(false)}
                >
                  {t('Close')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && submittedData && (
        <div className="sso-modal-overlay">
          <div className="sso-modal sso-success-modal">
            <button className="sso-modal-close" onClick={handleBackFromModal}>
              <Icon icon="close" size="medium" />
            </button>
            <div className="sso-modal-content">
              <h2 className="sso-modal-title">{t('request_submitted_title')}</h2>
              <p className="sso-modal-subtitle">
                {t('request_submitted_message')}
              </p>
              <div className="sso-request-number-box">
                <div className="sso-request-number-info">
                  <Icon icon="check_circle" size="medium" className="sso-request-number-icon" />
                  <div className="sso-request-number-text">
                    <span className="sso-request-number-label">{t('request_number_label')}</span>
                    <span className="sso-request-number-value">#{submittedData.requestNumber}</span>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(submittedData.requestNumber);
                    setCopyButtonText('Copied');
                    toast.success(t('Request number copied to clipboard'));
                    setTimeout(() => {
                      setCopyButtonText('Copy');
                    }, 5000);
                  }}
                >
                  <Icon icon={copyButtonText === 'Copied' ? 'check' : 'content_copy'} size="small" /> {t(copyButtonText)}
                </Button>
              </div>
              <p className="sso-modal-info-text">
                {t('See all your requests in the View Request section below.')}
              </p>
              <div className="sso-modal-actions">
                <Button
                  style={{minWidth:"auto"}}
                  variant="Tertiary"
                  size="medium"
                  onClick={handleBackFromModal}
                >
                  {t('Back')}
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleViewRequests}
                >
                  {t('view_requests_label')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer 
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default SSOOnboardingDetails;
